import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import logoutHandler from '../utils/controllers/logoutHandler';
import { API_URL } from '../config/Config';

// Create a base query with common settings
const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState, endpoint, extra, requestId }) => {
    // Include the access token in headers if available
    const publicEndpoints = ['getBrandingConfig'];
    if (publicEndpoints.includes(endpoint)) {
      return headers;
    }

    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
});

// Global variable to store the ongoing refresh promise
let refreshTokenPromise = null;

// Create a base query with token refresh handling
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.data?.errorObject?.errorCode === 401) {
    const oldRefreshToken = localStorage.getItem('refreshToken');

    // Third-party login (or missing refresh token): cannot renew — send user to login
    if (!oldRefreshToken) {
      logoutHandler();
      return result;
    }

    // If there's already a refresh in progress, wait for it
    if (refreshTokenPromise) {
      try {
        await refreshTokenPromise;
        // After the refresh is complete, retry the original request
        result = await baseQuery(args, api, extraOptions);
      } catch (error) {
        // If refresh failed, the promise will handle logout
        return result;
      }
    } else {
      // Start a new refresh process
      refreshTokenPromise = (async () => {
        try {
          const response = await baseQuery(
            `/User/renewToken?refreshToken=${oldRefreshToken}`,
            api,
            extraOptions
          );

          if (response?.data?.success) {
            const { accessToken, refreshToken } = response?.data?.data;
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            return true; // Success
          } else {
            // Handle token refresh failure by removing tokens and redirecting to login
            logoutHandler();
            throw new Error('Token refresh failed');
          }
        } finally {
          // Clear the promise when done (success or failure)
          refreshTokenPromise = null;
        }
      })();

      try {
        await refreshTokenPromise;
        // Retry the original request after successful refresh
        result = await baseQuery(args, api, extraOptions);
      } catch (error) {
        // Token refresh failed, return the original error
        return result;
      }
    }
  }

  return result;
};

// Create the API using Redux Toolkit's createApi
// A Connection agent's GET, with the format (and pairing) to narrow it to when one is given.
const connectionFormatQuery = (url, arg) => {
  const { agentId, format, pairing } = typeof arg === 'object' && arg ? arg : { agentId: arg };
  const params = new URLSearchParams({ agentId });
  if (format) params.set('format', format);
  if (format && pairing) params.set('pairing', pairing);
  return `${url}?${params}`;
};

export const bridgedApi = createApi({
  reducerPath: 'bridgedApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'collectionPush',
    'userInfo',
    'dashboard',
    'team',
    'cta',
    'eng',
    'domains',
    'pages',
    'agents',
    'collectionsV1',
    'collectionsV2',
    'collectionChunks',
    'actionBucket',
    'aiAgent',
    'smartReply',
    'onboarding',
    'aiLab',
    'folders',
    'fineTuning',
    'branding',
    'abTest',
    'webhooks',
    'webhookLogs',
    'Integrations',
    'events',
    'personalizationEmail',
    'connection',
    'labels',
  ],
  endpoints: builder => ({
    // ---------- User ------------
    // User registration mutation
    signUp: builder.mutation({
      query: data => ({
        url: '/User/Register',
        method: 'POST',
        body: data,
      }),
    }),

    // User login mutation
    login: builder.mutation({
      query: data => ({
        url: '/User/Login',
        method: 'POST',
        body: data,
      }),
    }),

    // ---------- Portal OAuth (connect Claude to the Bridged MCP) ------------
    // `origin` is the gateway the authorize request came from (checked in utils/oauth/portalOAuth)
    getPortalOAuthContext: builder.query({
      query: ({ origin, params }) => ({
        url: `${origin}/client/oauth/authorize/context`,
        method: 'GET',
        params,
      }),
      keepUnusedDataFor: 0,
    }),

    approvePortalOAuth: builder.mutation({
      query: ({ origin, params }) => ({
        url: `${origin}/client/oauth/authorize/approve`,
        method: 'POST',
        body: params,
      }),
    }),

    denyPortalOAuth: builder.mutation({
      query: ({ origin, params }) => ({
        url: `${origin}/client/oauth/authorize/deny`,
        method: 'POST',
        body: params,
      }),
    }),

    getBrandingConfig: builder.query({
      query: subdomain => ({
        url: `/api/public/branding?subdomain=${subdomain}`,
        method: 'GET',
      }),
      // This endpoint doesn't need auth, so we'll handle it specially
      transformResponse: response => {
        // Transform API response to match your structure
        return {
          logoUrl: response.logoUrl || '',
          primaryColor: response.primaryColor || '',
          companyName: response.companyName || '',
          subDomain: response.subDomain || '',
          playBooksSubscribed: response.playBooksSubscribed || [],
          brandingEnabled: !!(response.logoUrl || response.primaryColor || response.companyName),
        };
      },
      providesTags: ['branding'],
    }),

    // Resend verification email code mutation
    resendVerifyEmailCode: builder.mutation({
      query: data => ({
        url: '/User/ResendVerificationCode',
        method: 'POST',
        body: data,
      }),
    }),

    // Verify user's email mutation
    verifyUserEmail: builder.mutation({
      query: data => ({
        url: '/User/VerifyEmail',
        method: 'POST',
        body: data,
      }),
    }),

    // Forgot password verification mutation
    forgotPasswordVerification: builder.mutation({
      query: data => ({
        url: '/User/ForgotPassword',
        method: 'POST',
        body: data,
      }),
    }),

    // Forgot password mutation
    forgotPassword: builder.mutation({
      query: data => ({
        url: '/User/VerifyForgotCode',
        method: 'POST',
        body: data,
      }),
    }),

    // Google authentication query
    googleAuthentication: builder.query({
      query: accessToken => `/User/GoogleSignin?accessToken=${accessToken}`,
    }),

    // Facebook authentication query
    facebookAuthentication: builder.query({
      query: accessToken => `/User/FacebookSignin?accessToken=${accessToken}`,
    }),

    // ---------- Dashboard ------------

    dashboardOverview: builder.mutation({
      query: data => ({
        url: '/dashboard/Overview',
        method: 'POST',
        body: data,
      }),
    }),

    refreshDashboard: builder.mutation({
      query: () => ({
        url: '/Dashboard/RefreshDashboard',
        method: 'POST',
      }),
    }),

    // ------------------  Onboarding --------------

    getAllTasks: builder.query({
      query: () => '/Checklists/FindAll',
      providesTags: ['onboarding'],
    }),

    updateTask: builder.mutation({
      query: data => ({
        url: `Tasks/Completion?_id=${data._id}&isCompleted=${data.isCompleted}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['onboarding'],
    }),

    getTaskComments: builder.query({
      query: _id => `TaskComments/FindAllByTaskId/?_id=${_id}`,
      providesTags: ['onboarding'],
    }),

    createTaskComment: builder.mutation({
      query: data => ({
        url: 'TaskComments',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['onboarding'],
    }),

    updateTaskComment: builder.mutation({
      query: data => ({
        url: `TaskComments?_id=${data?.commentId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['onboarding'],
    }),

    deleteTaskComment: builder.mutation({
      query: _id => ({
        url: `TaskComments?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['onboarding'],
    }),

    // ------------------  User --------------

    userInfo: builder.query({
      query: () => '/User/Profile',
      providesTags: ['userInfo'],
    }),

    updateUserInfo: builder.mutation({
      query: data => ({
        url: '/User/Profile',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['userInfo'],
    }),

    deleteUserAccount: builder.mutation({
      query: () => ({
        url: '/User',
        method: 'DELETE',
      }),
    }),

    changeUserPassword: builder.mutation({
      query: data => ({
        url: '/User/ChangePassword',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['userInfo'],
    }),

    // ---------- CTAs ------------
    createCta: builder.mutation({
      query: data => ({
        url: '/Action',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['cta'],
    }),

    editCta: builder.mutation({
      query: data => ({
        url: `/Action`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['cta'],
    }),

    deleteCta: builder.mutation({
      query: _id => ({
        url: `/Action?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['cta'],
    }),

    duplicateCta: builder.mutation({
      query: _id => ({
        url: `/Action/duplicate?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['cta'],
    }),

    getAllUserCtas: builder.query({
      query: () => '/Action/UserAction',
      providesTags: ['cta'],
    }),

    getCtaById: builder.query({
      query: _id => `Action/FindById?_id=${_id}`,
      providesTags: ['cta'],
    }),

    // ---------- Engagements ------------

    createEng: builder.mutation({
      query: data => ({
        url: '/Engagement',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['eng'],
    }),

    editEng: builder.mutation({
      query: data => ({
        url: '/Engagement?constructionType=User_Generated',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['eng'],
    }),

    deleteEng: builder.mutation({
      query: _id => ({
        url: `/Engagement?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['eng'],
    }),

    duplicateEng: builder.mutation({
      query: _id => ({
        url: `/Engagement/duplicate?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['eng'],
    }),

    getAllUserEng: builder.query({
      query: () => 'Engagement/UserEngagement',
      providesTags: ['eng'],
    }),

    getEngById: builder.query({
      query: _id => `Engagement/findWithId?_id=${_id}`,
      providesTags: ['eng'],
    }),

    // ---------- AI Engagement ------------
    getAllAIEng: builder.query({
      query: ({ pageNo, approvalState, search }) =>
        `EngagementsApprovalQueue/FindAll?approvalState=${approvalState}&pageNumber=${pageNo}&search=${search}`,
      providesTags: ['eng'],
    }),

    deleteAiEngagement: builder.mutation({
      query: data => ({
        url: `/Engagement/DeleteEngagementWithCampaignPageId`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['eng'],
    }),

    approveAIEng: builder.mutation({
      query: id => ({
        url: `/EngagementsApprovalQueue/Approve?engagementApprovalQueueIdString=${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['eng'],
    }),

    unApproveAIEng: builder.mutation({
      query: id => ({
        url: `/EngagementsApprovalQueue/Reject?engagementApprovalQueueIdString=${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['eng'],
    }),

    regenerateAIEngagement: builder.mutation({
      query: id => ({
        url: `/EngagementsApprovalQueue/Regenerate?engagementApprovalQueueIdString=${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['eng'],
    }),

    editAIEng: builder.mutation({
      query: data => ({
        url: `/Engagement`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['eng'],
    }),

    // ---------- End Screen ------------
    createEndScreen: builder.mutation({
      query: data => ({
        url: '/EndScreen',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['endScreen'],
    }),

    editEndScreen: builder.mutation({
      query: data => ({
        url: '/EndScreen',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['endScreen'],
    }),

    deleteEndScreen: builder.mutation({
      query: _id => ({
        url: `/EndScreen?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['endScreen'],
    }),

    duplicateEndScreen: builder.mutation({
      query: _id => ({
        url: `/EndScreen/duplicate?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['endScreen'],
    }),

    getAllUserEndScreen: builder.query({
      query: () => 'EndScreen/FindAllCreatedUser',
      providesTags: ['endScreen'],
    }),

    getEndScreenById: builder.query({
      query: _id => `EndScreen/FindById?_id=${_id}`,
      providesTags: ['endScreen'],
    }),

    //  -------------- Collections V1 (actionBucket) ------------------
    createCollectionV1: builder.mutation({
      query: data => ({
        url: '/actionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    getAllUserCollections: builder.query({
      query: () => '/actionBucket',
      providesTags: ['collectionsV1'],
    }),

    deleteCollectionV1: builder.mutation({
      query: _id => ({
        url: `/actionBucket?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    deleteCollection: builder.mutation({
      query: collectionId => ({
        url: `/collections?collectionId=${collectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    getCollectionOverview: builder.query({
      query: _id => `/actionBucket/overview?actionBucketId=${_id}`,
      providesTags: ['collectionsV1'],
    }),

    updateAuthInfo: builder.mutation({
      query: data => ({
        url: '/actionBucket/updateAuthInfo',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    renameCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/RenameActionBucket',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    refreshCtaFromCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/RefreshActionFromActionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    deleteCtaFromCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/DeleteActionFromActionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    updateUseAsReference: builder.mutation({
      query: data => ({
        url: 'actionBucket/actionBucketPageData/UpdateUseAsReference',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    deleteMediaFromCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/DeleteMediaFromActionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    updateMediaUseAsReference: builder.mutation({
      query: data => ({
        url: 'actionBucket/actionBucketMediaData/UpdateUseAsReference',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    // Update media reference URL for an existing media file in Action Bucket
    updateMediaPreferredLink: builder.mutation({
      query: body => ({
        url: '/ActionBucket/actionBucketMediaData/preferredLinkAndName',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    addMediaToCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/AddMediaToActionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['actionBucket'],
    }),

    addPageToCollection: builder.mutation({
      query: data => ({
        url: '/actionBucket/AddPageToActionBucket',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    getWordpressPluginConnection: builder.query({
      query: id => `/WordpressPlugin/FindByDomainId?domainId=${id}`,
      providesTags: ['collectionsV1'],
    }),

    getAllSitemaps: builder.mutation({
      query: data => ({
        url: '/Domain/FetchSitemaps',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    refreshSitemap: builder.mutation({
      query: data => ({
        url: '/Domain/RefreshSitemaps',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    getXmlPageCrawlerStatus: builder.query({
      query: id => `/PageCrawler/xmlPageCrawlerStatus?domainId=${id}`,
      providesTags: ['collectionsV1'],
    }),

    startXMLCrawling: builder.mutation({
      query: data => ({
        url: '/PageCrawler/setupCrawler',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    addPageManuallyWithFile: builder.mutation({
      query: body => ({
        url: '/Page/addPageManuallyWithFile',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    getTargetPageList: builder.mutation({
      query: data => ({
        url: '/Page/getPagesByDomainId',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV1'],
    }),

    getWordpressFilters: builder.query({
      query: domainId => `/Page/getAvailablePageFilterValues?domainId=${domainId}`,
      providesTags: ['collectionsV1'],
    }),

    //  -------------- New Collection ------------------
    createCollection: builder.mutation({
      query: data => ({
        url: '/collections',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    listCollections: builder.query({
      query: (params = {}) => ({
        url: '/collections',
        method: 'GET',
        params,
      }),
      providesTags: ['collectionsV2'],
      transformResponse: response => {
        const data = response?.data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          const collections = Array.isArray(data.collections) ? data.collections : [];
          return {
            collections,
            total: data.total ?? collections.length,
          };
        }

        if (Array.isArray(data)) {
          return { collections: data, total: data.length };
        }

        if (Array.isArray(response?.collections)) {
          return {
            collections: response.collections,
            total: response.total ?? response.collections.length,
          };
        }

        return { collections: [], total: 0 };
      },
    }),

    getCollectionById: builder.query({
      query: _id => ({
        url: '/collections/GetById',
        method: 'GET',
        params: { _id },
      }),
      providesTags: ['collectionsV2'],
      transformResponse: response => {
        const collection = response?.data;
        if (collection && typeof collection === 'object' && !Array.isArray(collection)) {
          return collection;
        }
        if (response?._id) {
          return response;
        }
        return {};
      },
    }),

    addCollectionItems: builder.mutation({
      query: ({ collectionId, ...body }) => ({
        url: `/collections/items?collectionId=${collectionId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    addCollectionEntryPointItems: builder.mutation({
      query: ({ entryPointId, ...body }) => ({
        url: `/collections/entry-points/items?entryPointId=${entryPointId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    startCollectionIndexing: builder.mutation({
      query: ({ collectionId, ...body }) => ({
        url: `/collections/indexing/start?collectionId=${collectionId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    deleteCollectionItem: builder.mutation({
      query: ({ collectionId, itemId }) => ({
        url: `/collections/items`,
        method: 'DELETE',
        body: { collectionId, itemId },
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    deleteCollectionEntryPoint: builder.mutation({
      query: ({ collectionId, entryPointId }) => ({
        url: `/collections/entry-points`,
        method: 'DELETE',
        body: { collectionId, entryPointId },
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    refreshCollection: builder.mutation({
      query: collectionId => ({
        url: `/collections/refresh?collectionId=${collectionId}`,
        method: 'POST',
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    getCollectionItems: builder.query({
      query: ({ collectionId, ...params }) => ({
        url: `/collections/items?collectionId=${collectionId}`,
        method: 'GET',
        params,
      }),
      providesTags: ['collectionsV2'],
      transformResponse: response => {
        if (Array.isArray(response)) {
          return { items: response, entrypoints: [] };
        }

        const data = response?.data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          return {
            items: Array.isArray(data.items) ? data.items : [],
            entrypoints: Array.isArray(data.entrypoints)
              ? data.entrypoints
              : Array.isArray(data.entryPoints)
                ? data.entryPoints
                : [],
            total: data.total,
          };
        }

        if (Array.isArray(data)) {
          return { items: data, entrypoints: [] };
        }

        if (Array.isArray(response?.items) || Array.isArray(response?.entrypoints)) {
          return {
            items: Array.isArray(response.items) ? response.items : [],
            entrypoints: Array.isArray(response.entrypoints)
              ? response.entrypoints
              : Array.isArray(response.entryPoints)
                ? response.entryPoints
                : [],
            total: response.total,
          };
        }

        return { items: [], entrypoints: [] };
      },
    }),

    getCollectionsModuleOverview: builder.query({
      query: collectionId => ({
        url: `/collections/overview?collectionId=${collectionId}`,
        method: 'GET',
      }),
      providesTags: ['collectionsV2'],
      transformResponse: response => response?.data || response || {},
    }),

    // ---------------------- Event Database: attendee and event collections ----------------------
    // Rows of an attendee collection (people) or an event collection (sessions). The collections are
    // created, listed and deleted with the collection endpoints above (createCollection takes
    // `collectionType`; listCollections takes `collectionType` and `eventId`).
    getCollectionRecords: builder.query({
      query: ({ collectionId, ...params }) => ({
        url: `/collections/records?collectionId=${collectionId}`,
        method: 'GET',
        params,
      }),
      providesTags: ['collectionsV2'],
      transformResponse: response => response?.data || { records: [], total: 0, summary: {} },
    }),

    previewCollectionImport: builder.mutation({
      query: body => ({
        url: '/collections/records/import/preview',
        method: 'POST',
        body,
      }),
    }),

    importCollectionRecords: builder.mutation({
      query: body => ({
        url: '/collections/records/import',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['collectionsV2', 'connection'],
    }),

    addCollectionRecord: builder.mutation({
      query: ({ collectionId, ...body }) => ({
        url: `/collections/records?collectionId=${collectionId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['collectionsV2', 'connection'],
    }),

    editCollectionRecord: builder.mutation({
      query: body => ({
        url: '/collections/records',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['collectionsV2', 'connection'],
    }),

    deleteCollectionRecord: builder.mutation({
      query: body => ({
        url: '/collections/records',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    requestCollectionEnrichment: builder.mutation({
      query: collectionId => ({
        url: `/collections/enrichment/request?collectionId=${collectionId}`,
        method: 'POST',
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    // Push API (Salesforce or any CRM): the CRM calls POST /collections/records/push and
    // /collections/records/push/delete with the collection's x-api-key. These are the portal's routes
    // for the key, the field mapping and the recent calls.
    getCollectionPushSettings: builder.query({
      query: collectionId => ({
        url: `/collections/records/push/settings?collectionId=${collectionId}`,
        method: 'GET',
      }),
      providesTags: ['collectionPush'],
      transformResponse: response => response?.data ?? response ?? null,
    }),

    createCollectionPushKey: builder.mutation({
      query: collectionId => ({
        url: `/collections/records/push/key?collectionId=${collectionId}`,
        method: 'POST',
      }),
      invalidatesTags: ['collectionPush'],
      transformResponse: response => response?.data ?? response ?? null,
    }),

    deleteCollectionPushKey: builder.mutation({
      query: collectionId => ({
        url: `/collections/records/push/key?collectionId=${collectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['collectionPush'],
    }),

    // Body: { collectionId, columns: { [metadataKey]: field key | 'skip' }, attendeeTypeValues? }.
    // Saving re-applies the mapping to the rows that came in through the API.
    updateCollectionPushMapping: builder.mutation({
      query: body => ({
        url: '/collections/records/push/mapping',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['collectionPush', 'collectionsV2', 'connection'],
      transformResponse: response => response?.data ?? response ?? null,
    }),

    getCollectionPushLogs: builder.query({
      query: collectionId => ({
        url: `/collections/records/push/logs?collectionId=${collectionId}`,
        method: 'GET',
      }),
      providesTags: ['collectionPush'],
      transformResponse: response => {
        const data = response?.data ?? response;
        return Array.isArray(data) ? data : [];
      },
    }),

    startSitemapFetch: builder.mutation({
      query: ({ collectionId, entryPointId, limit = 1500 }) => ({
        url: `/collections/entry-points/sitemap/fetch`,
        method: 'POST',
        body: { collectionId, entryPointId, limit },
      }),
    }),

    getSitemapFetchStatus: builder.query({
      query: ({ collectionId, entryPointId }) => ({
        url: `/collections/entry-points/sitemap/fetch-status?collectionId=${collectionId}&entryPointId=${entryPointId}`,
        method: 'GET',
      }),
      transformResponse: response => response?.data || response || {},
    }),

    editCollection: builder.mutation({
      query: ({ collectionId, ...body }) => ({
        url: `/collections?collectionId=${collectionId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    editCollectionItem: builder.mutation({
      query: ({ collectionId, itemId, ...body }) => ({
        url: `/collections/items`,
        method: 'PATCH',
        body: { collectionId, itemId, ...body },
      }),
      invalidatesTags: ['collectionsV2'],
    }),

    // ---------- Labels (the team's own Knowledge Base labels, plus Bridged's defaults) ----------

    getLabels: builder.query({
      query: () => ({
        url: '/labels',
        method: 'GET',
      }),
      providesTags: ['labels'],
      transformResponse: response => (Array.isArray(response?.data) ? response.data : []),
    }),

    createLabel: builder.mutation({
      query: body => ({
        url: '/labels',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['labels'],
    }),

    updateLabel: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/labels/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['labels', 'collectionsV2'],
    }),

    // Removes the label from every item and every agent's recommendation types.
    deleteLabel: builder.mutation({
      query: id => ({
        url: `/labels/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['labels', 'collectionsV2', 'agents'],
    }),

    getCollectionItemChunks: builder.query({
      query: ({ itemId, page = 0, limit = 20 }) => ({
        url: '/collections/items/chunks',
        method: 'GET',
        params: { itemId, page, limit },
      }),
      providesTags: ['collectionChunks'],
      transformResponse: response => response?.data || { chunks: [], total: 0 },
    }),

    // Pre-signed S3 links are fetched directly: our Authorization header would break the signature.
    // The cache is keyed on the file paths, so re-signed links refetch in place instead of
    // starting an empty entry (which would flash the loading state on every chunk poll).
    getKnowledgeBaseChunkFiles: builder.query({
      // A skipped query passes `skipToken` instead of the list of links.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${
          Array.isArray(queryArgs) ? queryArgs.map(url => url.split('?')[0]).join('|') : ''
        })`,
      forceRefetch: ({ currentArg, previousArg }) =>
        Array.isArray(currentArg) &&
        currentArg.join('|') !== (Array.isArray(previousArg) ? previousArg.join('|') : ''),
      queryFn: async urls => {
        try {
          const files = await Promise.all(
            urls.map(async url => {
              const response = await fetch(url);
              if (!response.ok) throw new Error(`Chunk file request failed (${response.status})`);
              return response.json();
            })
          );
          return { data: files.flatMap(file => file?.data || []) };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
    }),

    editCollectionItemChunk: builder.mutation({
      query: body => ({
        url: '/collections/items/chunk',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['collectionChunks'],
    }),

    deleteCollectionItemChunk: builder.mutation({
      query: ({ itemId, chunkId }) => ({
        url: '/collections/items/chunk',
        method: 'DELETE',
        body: { itemId, chunkId },
      }),
      invalidatesTags: ['collectionChunks'],
    }),

    //  -------------- Team ------------------
    createTeam: builder.mutation({
      query: () => ({
        url: '/team',
        method: 'POST',
      }),
      invalidatesTags: ['team'],
    }),

    getTeams: builder.query({
      query: () => '/team',
      providesTags: ['team'],
    }),

    getTeamById: builder.query({
      query: _id => `/team/teamMember?teamId=${_id}`,
      providesTags: ['team'],
    }),

    inviteTeamMember: builder.mutation({
      query: body => ({
        url: '/team/teamMember',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['team'],
    }),

    updateTeamMember: builder.mutation({
      query: body => ({
        url: '/team/teamMember',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['team'],
    }),

    deleteTeamMember: builder.mutation({
      query: _id => ({
        url: `/team/teamMember?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['team'],
    }),

    getPendingTeamInvites: builder.query({
      query: () => '/team/Invites',
      providesTags: ['team'],
    }),

    acceptTeamInvite: builder.mutation({
      query: _id => ({
        url: `/team/AcceptTeamInvitation?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['team'],
    }),

    rejectTeamInvite: builder.mutation({
      query: _id => ({
        url: `/team/RejectTeamInvitation?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['team'],
    }),

    getTeamAccessableSections: builder.query({
      query: () => '/team/accessableSections',
      providesTags: ['team'],
    }),

    // ---------- Domains ------------

    getAllDomains: builder.query({
      query: () => '/Domain/FindAll',
      providesTags: ['domain'],
    }),

    createDomain: builder.mutation({
      query: body => ({
        url: '/Domain',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['domain'],
    }),

    verifyDomain: builder.mutation({
      query: id => ({
        url: `/Domain/Verify?_id=${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['domain'],
    }),

    deleteDomain: builder.mutation({
      query: _id => ({
        url: `/Domain?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['domain'],
    }),

    updatePageMetaData: builder.mutation({
      query: body => ({
        url: '/Domain/UpdatePageMetaDataConfigs',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['domain'],
    }),

    // ---------- Pages ------------

    getAllPages: builder.mutation({
      query: body => ({
        url: '/Page/FindPages',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['pages'],
    }),

    getSinglePageData: builder.query({
      query: _id => `/Page/FindPageByIdWithCampaignInfo?pageId=${_id}`,
      providesTags: ['pages'],
    }),

    excludePageFromCampaign: builder.mutation({
      query: body => ({
        url: `/campaign/ExcludePageFromCampaign`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['pages'],
    }),

    // ---------- Analytics ------------

    conversionRateChart: builder.mutation({
      query: ({ filters, type }) => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Performance/ConversionRateChart?top_performing_filter=${type}`,
        method: 'POST',
        body: filters,
      }),
    }),

    engagementRateChart: builder.mutation({
      query: ({ filters, type }) => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Performance/EngagementRateChart?top_performing_filter=${type}`,
        method: 'POST',
        body: filters,
      }),
    }),

    viewsChart: builder.mutation({
      query: ({ filters, type }) => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Performance/ViewsChart?top_performing_filter=${type}`,
        method: 'POST',
        body: filters,
      }),
    }),

    timeSpent: builder.mutation({
      query: ({ filters, type }) => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Performance/TimeSpent?top_performing_filter=${type}`,
        method: 'POST',
        body: filters,
      }),
    }),

    topActionCards: builder.mutation({
      query: body => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Performance/TopActionCards`,
        method: 'POST',
        body,
      }),
    }),

    topEngagementCards: builder.mutation({
      query: body => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Performance/TopEngagementCards`,
        method: 'POST',
        body,
      }),
    }),

    kaReferenceOverview: builder.mutation({
      query: body => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Performance/KaReferenceOverview`,
        method: 'POST',
        body,
      }),
    }),

    topEndScreenCards: builder.mutation({
      query: body => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Performance/TopEndScreenCards`,
        method: 'POST',
        body,
      }),
    }),

    timeLineChart: builder.mutation({
      query: body => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Performance/TimeLineChart`,
        method: 'POST',
        body,
      }),
    }),

    topPerformingPages: builder.mutation({
      query: ({ filters, type }) => ({
        url: `/Analytics/Performance/TopPerformingPages?filter=${type}`,
        method: 'POST',
        body: filters,
      }),
    }),

    performanceChart: builder.mutation({
      query: filters => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Users/PerformanceChart`,
        method: 'POST',
        body: filters,
      }),
    }),

    countriesChart: builder.mutation({
      query: filters => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Users/Countries`,
        method: 'POST',
        body: filters,
      }),
    }),

    citiesChart: builder.mutation({
      query: filters => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Users/Cities`,
        method: 'POST',
        body: filters,
      }),
    }),

    browsersChart: builder.mutation({
      query: filters => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Users/Browsers`,
        method: 'POST',
        body: filters,
      }),
    }),

    devicesChart: builder.mutation({
      query: filters => ({
        url: `https://staging-ai-insights-and-trends-fekv67xtza-ew.a.run.app/Users/Devices`,
        method: 'POST',
        body: filters,
      }),
    }),

    // ---------- Agents ------------

    userConfiguration: builder.query({
      query: () => '/UserConfiguration',
      providesTags: ['agents'],
    }),

    allAgents: builder.query({
      query: () => '/Campaign/FindAllUserCampaigns',
      providesTags: ['agents'],
    }),

    getAgentById: builder.query({
      query: id => `/Campaign/FindCampaignByIdForUpdate?campaignId=${id}`,
      providesTags: ['agents'],
    }),

    createAgent: builder.mutation({
      query: body => ({
        url: '/Campaign/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['agents'],
    }),

    updateAgent: builder.mutation({
      query: body => ({
        url: '/Campaign/UpdateCampaign',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['agents'],
    }),

    deleteAgent: builder.mutation({
      query: _id => ({
        url: `/Campaign/?campaignId=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['agents'],
    }),

    editAgent: builder.mutation({
      query: body => ({
        url: '/Campaign/UpdateCampaign',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['agents'],
    }),

    duplicateAgent: builder.mutation({
      query: id => ({
        url: `campaign/duplicate?campaignId=${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['agents'],
    }),

    editAgentState: builder.mutation({
      query: id => ({
        url: `/Campaign/ToggleState?campaignId=${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['agents'],
    }),

    createScreenFromTemplate: builder.mutation({
      query: body => ({
        url: '/Template',
        method: 'POST',
        body,
      }),
    }),

    getAgentOverviewCacheStatus: builder.mutation({
      query: body => ({
        url: '/Campaign/GetOverviewCacheStatus',
        method: 'POST',
        body,
      }),
    }),

    refreshAgentOverview: builder.mutation({
      query: body => ({
        url: '/Campaign/RefreshOverview',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewSummary: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/Summary',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewVoice: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/Voice',
        method: 'POST',
        body,
      }),
    }),

    // "Hear it" in the Voice drawer: { text (1–500), lang (Voice setting) } → audio/mpeg Blob.
    // Error answers stay JSON so the token refresh above still sees a 401.
    voiceSpeechPreview: builder.mutation({
      query: body => ({
        url: '/Campaign/voice/speech-preview',
        method: 'POST',
        body,
        responseHandler: async response => {
          if (response.ok) return response.blob();
          const text = await response.text();
          try {
            return JSON.parse(text);
          } catch {
            return text;
          }
        },
      }),
    }),

    downloadPersonalizationToolRegistrationLeads: builder.mutation({
      query: body => ({
        url: '/campaign/personalizationTool/registerationLeads',
        method: 'POST',
        body,
      }),
    }),

    agentActivity: builder.mutation({
      query: body => ({
        url: '/activity/campaignActivity',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTimeline: builder.mutation({
      query: body => ({
        url: '/Campaign/OverviewTimeline',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTopEngagements: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/TopEngagements',
        method: 'POST',
        body,
      }),
    }),
    whatsAppOverviewTopEngagements: builder.mutation({
      query: body => ({
        url: '/SmartReply/Overview/TopEngagements/Pagination',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTopEngagementsPagination: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/TopEngagements/Pagination',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTopEngagementsDownload: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/TopEngagements/Download',
        method: 'POST',
        body,
      }),
    }),

    getKAReferenceOverview: builder.mutation({
      query: body => ({
        url: '/Campaign/GetKaReferenceOverview',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTopActions: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/TopActions',
        method: 'POST',
        body,
      }),
    }),

    agentIRISSessions: builder.mutation({
      query: body => ({
        url: '/Campaign/GetIRISSessions',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewWordCloud: builder.mutation({
      query: body => ({
        url: '/Campaign/GetWordCloud',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewWordCloudDownload: builder.mutation({
      query: body => ({
        url: '/Campaign/DownloadRagResult',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewFeedback: builder.mutation({
      query: body => ({
        url: '/Campaign/GetCampaignOverviewFeedback',
        method: 'POST',
        body,
      }),
    }),

    downloadAgentOverviewFeedback: builder.mutation({
      query: body => ({
        url: '/Campaign/DonwloadCampaignOverviewFeedback',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewPages: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/Pages',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewPagesDownload: builder.mutation({
      query: id => ({
        url: `/Campaign/Overview/Pages/download/?campaignId=${id}`,
        method: 'POST',
      }),
    }),

    agentOverviewTopVisitors: builder.query({
      query: ({ campaignId, pageNumber, startDate, endDate, limit }) =>
        `Campaign/Overview/Visitors?campaignId=${campaignId}&pageNumber=${pageNumber}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
      invalidatesTags: ['agents'],
    }),

    agentOverviewTopVisitorsDynamicForm: builder.mutation({
      query: body => ({
        url: '/Campaign/Overview/actionTakerDynamicTable',
        method: 'POST',
        body,
      }),
    }),

    agentOverviewTopVisitorsJourneyCtas: builder.query({
      query: campaignId => ({
        url: `/Campaign/getCampaignActions?campaignId=${campaignId}`,
        method: 'GET',
      }),
    }),

    uploadFont: builder.mutation({
      query: body => ({
        url: '/Media/font',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['fonts'],
    }),

    getAllFontHistory: builder.query({
      query: () => '/Media/FontsHistory',
      providesTags: ['fonts'],
    }),

    agentOverviewTopVisitorsDownload: builder.mutation({
      query: body => ({
        // url: '/Campaign/Overview/Visitors/Download/',
        url: '/Campaign/Overview/actionTakerDynamicTable/Download',
        method: 'POST',
        body,
      }),
    }),

    AIGeneratedExamples: builder.mutation({
      query: body => ({
        url: '/Engagement/AIGeneratedExample',
        method: 'POST',
        body,
      }),
    }),

    getDomainConfigHistory: builder.query({
      query: _id => `/Campaign/GetDomainConfigHistory`,
      providesTags: ['campaign'],
    }),

    clearFAQCache: builder.mutation({
      query: body => ({
        url: '/campaign/ClearFAQCache',
        method: 'POST',
        body,
      }),
    }),

    // FAQ keywords: the team's legacy status and each use case's default keywords.
    getFAQKeywordConfig: builder.query({
      query: () => '/campaign/FAQKeywordConfig',
    }),

    // ---------------------- AI Brand Sync ----------------------
    domainBrandSync: builder.query({
      query: domainId => ({
        url: `/Domain/BrandSync?domainId=${domainId}`,
        method: 'GET',
      }),
    }),

    getAIBrandSyncSuggestions: builder.mutation({
      query: body => ({
        url: '/Domain/BrandSync/Suggest',
        method: 'POST',
        body,
      }),
    }),

    // ---------------------- AI Agent ----------------------

    AllAIAgents: builder.query({
      query: () => '/aiAgent/GetUserAIAGents',
      providesTags: ['aiAgent', 'agents'],
    }),

    getAIAgentById: builder.query({
      query: id => `/aiAgent/GetUserAIAGentById?id=${id}`,
      providesTags: ['aiAgent', 'agents'],
    }),

    createAIAgent: builder.mutation({
      query: body => ({
        url: '/aiAgent/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['aiAgent', 'agents'],
    }),

    updateAIAgent: builder.mutation({
      query: body => ({
        url: '/aiAgent/update',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['aiAgent', 'agents'],
    }),

    deleteAIAgent: builder.mutation({
      query: _id => ({
        url: `/aiAgent/Delete?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['aiAgent', 'agents'],
    }),

    // ---------------------- Smart Reply ----------------------

    getAllSmartReplies: builder.query({
      query: () => '/SmartReply/Agents/',
      providesTags: ['smartReply'],
    }),

    getSmartReplyById: builder.query({
      query: _id => `/SmartReply/Agent/?_id=${_id}`,
      providesTags: ['smartReply'],
    }),

    createSmartReply: builder.mutation({
      query: body => ({
        url: '/SmartReply/Agent/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['smartReply', 'agents', 'aiAgent'],
    }),

    updateSmartReply: builder.mutation({
      query: ({ _id, ...body }) => ({
        url: `/SmartReply/Agent/?_id=${_id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['smartReply', 'agents', 'aiAgent'],
    }),

    deleteSmartReply: builder.mutation({
      query: _id => ({
        url: `/SmartReply/Agent/?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['smartReply', 'agents', 'aiAgent'],
    }),

    // ---------------------- Smart Reply Overview ----------------------

    getSmartReplyOverview: builder.query({
      query: ({ _id, startDate, endDate }) =>
        `/SmartReply/Agent/Overview/?_id=${_id}&startDate=${startDate}&endDate=${endDate}`,
      providesTags: ['smartReply'],
    }),

    getSmartReplyOverviewTimeline: builder.query({
      query: ({ _id, startDate, endDate }) =>
        `/SmartReply/Agent/Timeline?_id=${_id}&startDate=${startDate}&endDate=${endDate}`,
      providesTags: ['smartReply'],
    }),

    getSmartReplyEmailLogs: builder.query({
      query: _id => `/SmartReply/Agent/Emails?agentId=${_id}`,
      providesTags: ['smartReply'],
    }),

    getSmartReplyEmailThread: builder.query({
      query: _id => `/SmartReply/Agent/Email?_id=${_id}`,
      providesTags: ['smartReply'],
    }),

    // ---------------------- Smart Reply Whatsapp Overview ----------------------
    getSmartReplyWhatsappOverview: builder.mutation({
      query: body => ({
        url: '/SmartReply/Agent/Overview',
        method: 'POST',
        body,
      }),
      providesTags: ['smartReply'],
    }),
    setSmartReplyBroadcast: builder.mutation({
      query: body => ({
        url: '/smartReply/schedules',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        { type: 'smartReply', id: body.smartReplyAgentId },
      ],
    }),
    getSmartReplyBroadcastList: builder.query({
      query: id => ({
        url: `/smartReply/schedules?smartReplyAgentId=${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'smartReply', id }],
    }),
    deleteSmartReplyBroadcast: builder.mutation({
      query: id => ({
        url: `/smartReply/schedules?id=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['smartReply'],
    }),

    // ---------------------- Personalization Email Channel ----------------------
    getPtTargetList: builder.query({
      query: ({ agentId, pageNumber, limit, ...filters }) => ({
        url: `/targetList/search?agentId=${agentId}`,
        method: 'POST',
        body: { pageNumber, limit, ...filters },
      }),
      providesTags: ['personalizationEmail'],
    }),

    getPtTargetListMetaDataFields: builder.query({
      query: ({ agentId }) => `/targetList/metadata?agentId=${agentId}`,
      providesTags: ['personalizationEmail'],
    }),

    importProfilesFromCsv: builder.mutation({
      query: body => ({
        url: '/PersonalizationTool/profile/import/csv',
        method: 'POST',
        body,
      }),
    }),

    getCsvImportColumns: builder.query({
      query: mediaId => `/PersonalizationTool/profile/import/csv/columns?mediaId=${mediaId}`,
    }),

    importProfilesFromHubspot: builder.mutation({
      query: body => ({
        url: '/PersonalizationTool/profile/import/hubspot',
        method: 'POST',
        body,
      }),
    }),

    getProfileImportStatus: builder.query({
      query: id => ({
        url: `/PersonalizationTool/profile/import/status/?importId=${id}`,
        method: 'POST',
      }),
      providesTags: (result, error, id) => [{ type: 'personalizationEmail', id }],
    }),

    getPersonalizationProfileList: builder.query({
      query: body => ({
        url: '/personalizationTool/profile/list',
        method: 'POST',
        body,
      }),
      providesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: `profiles-${args?.importId}` },
      ],
    }),

    getCrmIntegrationsList: builder.query({
      query: () => ({
        url: '/integration?type=hubspot',
        method: 'GET',
      }),
      providesTags: ['personalizationEmail'],
    }),

    getIntegrationHubProperties: builder.query({
      query: integrationId => `/integration/hubspot/properties?integrationId=${integrationId}`,
      providesTags: (result, error, integrationId) => [
        { type: 'personalizationEmail', id: `properties-${integrationId}` },
      ],
    }),

    createHubspotProperty: builder.mutation({
      query: body => ({
        url: '/integration/hubspot/properties',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        { type: 'personalizationEmail', id: `properties-${body?.integrationId}` },
      ],
    }),

    connectEmailChannelHubspot: builder.mutation({
      query: body => ({
        url: '/Integration',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    deleteCrmIntegration: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration?integrationId=${integrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    verifyHubspotIntegration: builder.mutation({
      query: id => ({
        url: `/integration/hubspot/verify?integrationId=${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    getPersonalizationAgents: builder.query({
      query: () => '/PersonalizationTool/Agent/List',
      providesTags: ['personalizationEmail'],
    }),

    createPersonalizationAgent: builder.mutation({
      query: body => ({
        url: '/PersonalizationTool/Agent',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    previewPersonalizationAgent: builder.mutation({
      query: agentId => ({
        url: `/PersonalizationTool/Agent/Preview?agentId=${agentId}`,
        method: 'POST',
      }),
    }),

    startPersonalizationAgent: builder.mutation({
      query: agentId => ({
        url: `/PersonalizationTool/Agent/Start?agentId=${agentId}`,
        method: 'POST',
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    getPersonalizationAgent: builder.query({
      query: agentId => `/PersonalizationTool/Agent/overview?agentId=${agentId}`,
      providesTags: (result, error, agentId) => [{ type: 'personalizationEmail', id: agentId }],
    }),

    deletePersonalizationAgent: builder.mutation({
      query: agentId => ({
        url: `/PersonalizationTool/Agent?agentId=${agentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['personalizationEmail'],
    }),

    getPersonalizationAgentProfiles: builder.query({
      query: body => ({
        url: '/PersonalizationTool/Agent/overview/Profiles',
        method: 'POST',
        body,
      }),
      providesTags: (result, error, args) => [{ type: 'personalizationEmail', id: args?.agentId }],
    }),

    getPersonalizationAgentProfile: builder.query({
      query: ({ agentId, profileId }) =>
        `/PersonalizationTool/Agent/Profile?agentId=${agentId}&profileId=${profileId}`,
      providesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: args?.profileId },
      ],
    }),

    retryPersonalizationAgentFailedProfiles: builder.mutation({
      query: ({ agentId, profileIds }) => ({
        url: '/PersonalizationTool/Agent/RetryFailed',
        method: 'POST',
        body: { agentId, profileIds },
      }),
      invalidatesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: args?.agentId },
      ],
    }),

    downloadPersonalizationAgentExport: builder.mutation({
      query: agentId => `/PersonalizationTool/Agent/Download?agentId=${agentId}`,
    }),

    publishPersonalizationAgentIntegration: builder.mutation({
      query: body => ({
        url: '/PersonalizationTool/Agent/PublishIntegration/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: args?.agentId },
        { type: 'personalizationEmail', id: `deliveries-${args?.agentId}` },
        'personalizationEmail',
      ],
    }),

    getPersonalizationAgentPublishStatus: builder.query({
      query: publishId =>
        `/PersonalizationTool/Agent/PublishIntegration/Status?publishId=${publishId}`,
    }),

    getPersonalizationAgentDeliveries: builder.query({
      query: ({ agentId, page, pageSize }) =>
        `/PersonalizationTool/Agent/Deliveries?agentId=${agentId}&page=${page}&pageSize=${pageSize}`,
      providesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: `deliveries-${args?.agentId}` },
        'personalizationEmail',
      ],
    }),

    // ---------------------- Connection agent ----------------------
    createConnectionAgent: builder.mutation({
      query: body => ({
        url: '/Connection/Agent',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection', 'agents'],
    }),

    getConnectionAgent: builder.query({
      query: agentId => `/Connection/Agent/overview?agentId=${agentId}`,
      providesTags: (result, error, agentId) => [{ type: 'connection', id: agentId }],
    }),

    updateConnectionAgent: builder.mutation({
      query: body => ({
        url: '/Connection/Agent',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    deleteConnectionAgent: builder.mutation({
      query: agentId => ({
        url: `/Connection/Agent?agentId=${agentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['connection', 'agents'],
    }),

    // Duplicate to another event: a draft with the agent's setup and none of its people or matches.
    duplicateConnectionAgent: builder.mutation({
      query: body => ({
        url: '/Connection/Agent/Duplicate',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection', 'agents'],
    }),

    startConnectionRun: builder.mutation({
      query: body => ({
        url: '/Connection/Run',
        method: 'POST',
        body,
      }),
      // 'agents': the agents list says what's waiting to go to the app.
      invalidatesTags: ['connection', 'agents'],
    }),

    getConnectionRuns: builder.query({
      query: agentId => `/Connection/Run/List?agentId=${agentId}`,
      providesTags: ['connection'],
    }),

    publishConnectionAgent: builder.mutation({
      query: agentId => ({
        url: `/Connection/Publish?agentId=${agentId}`,
        method: 'POST',
      }),
      // 'agents': the agents list says what's waiting to go to the app.
      invalidatesTags: ['connection', 'agents'],
    }),

    getConnectionMatches: builder.query({
      query: body => ({
        url: '/Connection/Match/List',
        method: 'POST',
        body,
      }),
      providesTags: ['connection'],
    }),

    addConnectionMatch: builder.mutation({
      query: body => ({
        url: '/Connection/Match',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    updateConnectionMatch: builder.mutation({
      query: body => ({
        url: '/Connection/Match',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    deleteConnectionMatch: builder.mutation({
      query: matchId => ({
        url: `/Connection/Match?matchId=${matchId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['connection'],
    }),

    getConnectionPeople: builder.query({
      query: body => ({
        url: '/Connection/People/List',
        method: 'POST',
        body,
      }),
      providesTags: ['connection'],
    }),

    // Sponsor companies (rules.companies): each with its reps, lead, details and the team's numbers.
    getConnectionCompanies: builder.query({
      query: agentId => `/Connection/Companies?agentId=${agentId}`,
      providesTags: ['connection'],
    }),

    // A company's details, or its lead (one of its reps, or null).
    updateConnectionCompany: builder.mutation({
      query: body => ({
        url: '/Connection/Company',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    sendConnectionReminder: builder.mutation({
      query: body => ({
        url: '/Connection/People/Remind',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    removeConnectionPerson: builder.mutation({
      query: ({ agentId, personId }) => ({
        url: `/Connection/People?agentId=${agentId}&personId=${personId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['connection'],
    }),

    getConnectionMeetings: builder.query({
      query: body => ({
        url: '/Connection/Meeting/List',
        method: 'POST',
        body,
      }),
      providesTags: ['connection'],
    }),

    updateConnectionMeeting: builder.mutation({
      query: body => ({
        url: '/Connection/Meeting',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    // ---- Connection agent: the agent's page (Overview, Agenda, roundtable groups, People, Settings) ----
    // The round's numbers per run and engine version, readiness, requests by status and the Get ready checklist.
    // Takes the agent id, or `{ agentId, format, pairing }` to narrow it to one format.
    getConnectionOverview: builder.query({
      query: arg => connectionFormatQuery('/Connection/Overview', arg),
      providesTags: ['connection'],
    }),

    // The agent's sessions by day (its event collection, plus sessions added here), who has each, invitations, feedback.
    getConnectionAgenda: builder.query({
      query: agentId => `/Connection/Agenda?agentId=${agentId}`,
      providesTags: ['connection'],
    }),

    createConnectionSession: builder.mutation({
      query: body => ({
        url: '/Connection/Session',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    updateConnectionSession: builder.mutation({
      query: body => ({
        url: '/Connection/Session',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    deleteConnectionSession: builder.mutation({
      query: ({ agentId, index }) => ({
        url: `/Connection/Session?agentId=${agentId}&index=${index}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['connection'],
    }),

    // Roundtable groups (one to many): each group with its members and seats.
    getConnectionGroups: builder.query({
      query: agentId => `/Connection/Group/List?agentId=${agentId}`,
      providesTags: ['connection'],
    }),

    moveConnectionGroupMember: builder.mutation({
      query: body => ({
        url: '/Connection/Group/Member',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    replaceConnectionPeerSuggestions: builder.mutation({
      query: body => ({
        url: '/Connection/Match/ReplacePeers',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    addConnectionPerson: builder.mutation({
      query: body => ({
        url: '/Connection/People',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    updateConnectionPerson: builder.mutation({
      query: body => ({
        url: '/Connection/People',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    createConnectionSignInLink: builder.mutation({
      query: body => ({
        url: '/Connection/People/SignInLink',
        method: 'POST',
        body,
      }),
    }),

    resetConnectionPerson: builder.mutation({
      query: body => ({
        url: '/Connection/People/Reset',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    // The event, the Connection app address, the time zone, peer limits and the event admins (the Settings tab).
    getConnectionSettings: builder.query({
      query: agentId => `/Connection/Settings?agentId=${agentId}`,
      providesTags: ['connection'],
    }),

    addConnectionAdmin: builder.mutation({
      query: body => ({
        url: '/Connection/Admin',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    removeConnectionAdmin: builder.mutation({
      query: adminId => ({
        url: `/Connection/Admin?adminId=${adminId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['connection'],
    }),

    resetConnectionRound: builder.mutation({
      query: body => ({
        url: '/Connection/Round/Reset',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),
    // ---- end of the agent's page ----

    // One request: who asked, the note, who cancelled and when, and its history (newest first).
    getConnectionMeeting: builder.query({
      query: meetingId => `/Connection/Meeting?meetingId=${meetingId}`,
      providesTags: ['connection'],
    }),

    // The sponsor's open times for a request, each checked for a clash with the delegate's other meetings.
    getConnectionMeetingSlots: builder.query({
      query: meetingId => `/Connection/Meeting/Slots?meetingId=${meetingId}`,
      providesTags: ['connection'],
    }),

    // Deletes a request: the pick goes back on the shortlist and a booked time reopens.
    deleteConnectionMeeting: builder.mutation({
      query: meetingId => ({
        url: `/Connection/Meeting?meetingId=${meetingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['connection'],
    }),

    // Auto-assign into breaks: who would go where, and who can't be placed. Changes nothing.
    previewConnectionAutoAssign: builder.mutation({
      query: body => ({
        url: '/Connection/Meeting/AutoAssign/Preview',
        method: 'POST',
        body,
      }),
    }),

    // Books the preview's meetings; `confirmCount` must be the number placed.
    runConnectionAutoAssign: builder.mutation({
      query: body => ({
        url: '/Connection/Meeting/AutoAssign',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    // One matching run's results: confidence, low signal, who it left out and couldn't match, its matches.
    getConnectionRun: builder.query({
      query: runId => `/Connection/Run?runId=${runId}`,
      providesTags: ['connection'],
    }),

    // What both sides said after their meetings, and thumbs on programme sessions (the Feedback tab).
    getConnectionFeedback: builder.query({
      query: agentId => `/Connection/Feedback?agentId=${agentId}`,
      providesTags: ['connection'],
    }),

    // The event report, counted from the whole event (the Event report tab).
    // Takes the agent id, or `{ agentId, format, pairing }` to narrow it to one format.
    getConnectionReport: builder.query({
      query: arg => connectionFormatQuery('/Connection/Report', arg),
      providesTags: ['connection'],
    }),

    getConnectionDeliveries: builder.query({
      query: agentId => `/Connection/Delivery/List?agentId=${agentId}`,
      providesTags: ['connection'],
    }),

    createConnectionDelivery: builder.mutation({
      query: body => ({
        url: '/Connection/Delivery',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    cancelConnectionDelivery: builder.mutation({
      query: deliveryId => ({
        url: `/Connection/Delivery/Cancel?deliveryId=${deliveryId}`,
        method: 'POST',
      }),
      invalidatesTags: ['connection'],
    }),

    // Deliveries: one delivery with every person's result on each channel; drafts, edits, copies and retries.
    getConnectionDelivery: builder.query({
      query: deliveryId => `/Connection/Delivery?deliveryId=${deliveryId}`,
      providesTags: ['connection'],
    }),

    // Who a delivery would reach right now: the group's size, a sample, and reach per channel.
    getConnectionDeliveryAudience: builder.query({
      query: body => ({
        url: '/Connection/Delivery/Audience',
        method: 'POST',
        body,
      }),
      providesTags: ['connection'],
    }),

    updateConnectionDelivery: builder.mutation({
      query: body => ({
        url: '/Connection/Delivery',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    deleteConnectionDelivery: builder.mutation({
      query: deliveryId => ({
        url: `/Connection/Delivery?deliveryId=${deliveryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['connection'],
    }),

    sendConnectionDeliveryNow: builder.mutation({
      query: deliveryId => ({
        url: `/Connection/Delivery/SendNow?deliveryId=${deliveryId}`,
        method: 'POST',
      }),
      invalidatesTags: ['connection'],
    }),

    copyConnectionDelivery: builder.mutation({
      query: deliveryId => ({
        url: `/Connection/Delivery/Copy?deliveryId=${deliveryId}`,
        method: 'POST',
      }),
      invalidatesTags: ['connection'],
    }),

    retryConnectionDelivery: builder.mutation({
      query: body => ({
        url: '/Connection/Delivery/Retry',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    sendConnectionDeliveryForReal: builder.mutation({
      query: deliveryId => ({
        url: `/Connection/Delivery/SendForReal?deliveryId=${deliveryId}`,
        method: 'POST',
      }),
      invalidatesTags: ['connection'],
    }),

    fixConnectionPersonContact: builder.mutation({
      query: body => ({
        url: '/Connection/People/Contact',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    // Channels: what each needs and whether it's ready. WhatsApp numbers and HubSpot accounts are picked from
    // Integrations (D39), never connected here.
    getConnectionChannels: builder.query({
      query: agentId => `/Connection/Channels?agentId=${agentId}`,
      providesTags: ['connection'],
    }),

    updateConnectionChannel: builder.mutation({
      query: body => ({
        url: '/Connection/Channels',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    verifyConnectionSenderDomain: builder.mutation({
      query: agentId => ({
        url: `/Connection/Channels/VerifyDomain?agentId=${agentId}`,
        method: 'POST',
      }),
      invalidatesTags: ['connection'],
    }),

    requestConnectionWhatsappTemplate: builder.mutation({
      query: body => ({
        url: '/Connection/Channels/WhatsappTemplate',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    checkConnectionWhatsappTemplates: builder.mutation({
      query: agentId => ({
        url: `/Connection/Channels/WhatsappTemplate/Check?agentId=${agentId}`,
        method: 'POST',
      }),
      invalidatesTags: ['connection'],
    }),

    // Automatic messages, pause, test mode and the allow list, calendar invites and the daily summary.
    getConnectionMessaging: builder.query({
      query: agentId => `/Connection/Messaging?agentId=${agentId}`,
      providesTags: ['connection'],
    }),

    updateConnectionMessaging: builder.mutation({
      query: body => ({
        url: '/Connection/Messaging',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    updateConnectionAutoMessage: builder.mutation({
      query: body => ({
        url: '/Connection/Messaging/Message',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    sendConnectionMessagingTest: builder.mutation({
      query: body => ({
        url: '/Connection/Messaging/Test',
        method: 'POST',
        body,
      }),
    }),

    // The pending actions email: one email to everybody with something waiting in their hub.
    getConnectionPendingActions: builder.query({
      query: agentId => `/Connection/PendingActions?agentId=${agentId}`,
      providesTags: ['connection'],
    }),

    sendConnectionPendingActions: builder.mutation({
      query: body => ({
        url: '/Connection/PendingActions',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['connection'],
    }),

    // Starts a simulation and answers at once with { testId, status: 'running', startedAt };
    // the result is read with getMatchingEngineTest.
    simulateMatchingEngine: builder.mutation({
      query: body => ({
        url: '/AILab/Matching/Simulate',
        method: 'POST',
        body,
      }),
    }),

    getPTInputLogs: builder.query({
      query: ({ agentId, pageNumber, limit, startDate, endDate, hasRegistration }) => {
        const params = new URLSearchParams({ agentId });
        if (pageNumber != null) params.set('pageNumber', String(pageNumber));
        if (limit != null) params.set('limit', String(limit));
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        if (hasRegistration !== undefined) {
          params.set('hasRegistration', hasRegistration ? 'true' : 'false');
        }
        return `/Campaign/personalizationToolOverview/inputLogs?${params}`;
      },
      providesTags: (result, error, args) => [
        { type: 'personalizationEmail', id: `pt-input-logs-${args?.agentId}` },
      ],
    }),

    getPersonalizationById: builder.query({
      query: id => `/Campaign/personalizationById?id=${id}`,
      providesTags: (result, error, id) => [
        { type: 'personalizationEmail', id: `pt-journey-${id}` },
      ],
    }),

    getPTTopChunks: builder.query({
      query: ({ agentId, chunksPerLabel = 5, startDate, endDate }) => {
        const params = new URLSearchParams({
          agentId,
          chunksPerLabel: String(chunksPerLabel),
        });
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        return `/Campaign/personalizationToolOverview/topChunks?${params}`;
      },
      refetchOnMountOrArgChange: true,
    }),

    getPTFunnelOverview: builder.query({
      query: ({ agentId, startDate, endDate }) => {
        const params = new URLSearchParams({ agentId });
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        return `/Campaign/personalizationToolOverview/funnelOverview?${params}`;
      },
      refetchOnMountOrArgChange: true,
    }),

    getPTRecommendationTypes: builder.query({
      query: ({ agentId, startDate, endDate }) => {
        const params = new URLSearchParams({ agentId });
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        return `/Campaign/personalizationToolOverview/recommendationTypes?${params}`;
      },
      refetchOnMountOrArgChange: true,
    }),

    // ---------------------- Qualification ----------------------

    getAllQualificationAgents: builder.query({
      query: () => '/QualificationAgent/FindAll',
      providesTags: ['qualification'],
    }),

    createQualification: builder.mutation({
      query: body => ({
        url: '/QualificationAgent/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    getQualificationById: builder.query({
      query: _id => `/QualificationAgent/?_id=${_id}`,
      providesTags: ['qualification'],
    }),

    updateQualification: builder.mutation({
      query: ({ _id, ...body }) => ({
        url: `/QualificationAgent/?_id=${_id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    deleteQualification: builder.mutation({
      query: _id => ({
        url: `/QualificationAgent/?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    // ---------------------- Qualificaiton agent v1 ----------------

    getAllQualificationAgentsV1: builder.query({
      query: () => '/QualificationAgent/v1/FindAll',
      providesTags: ['qualification', 'agents'],
    }),

    createQualificationAgentV1: builder.mutation({
      query: body => ({
        url: '/QualificationAgent/v1/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    updateQualificationAgentV1: builder.mutation({
      query: ({ agentId, body }) => ({
        url: `/QualificationAgent/v1/?agentId=${agentId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    getQualificationAgentV1ById: builder.query({
      query: agentId => `/QualificationAgent/v1/?agentId=${agentId}`,
      providesTags: ['qualification', 'agents'],
    }),

    deleteQualificationAgentV1: builder.mutation({
      query: agentId => ({
        url: `/QualificationAgent/v1/?agentId=${agentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['qualification', 'agents'],
    }),

    // ---------------------- Qualification Overview ----------------------
    getQualificationOverview: builder.query({
      query: _id => `/QualificationAgent/Overview/?_id=${_id}`,
      providesTags: ['qualification'],
    }),

    getQualificationAudience: builder.query({
      query: ({ _id, stageType, pageNumber, withEmailOnly }) =>
        `/QualificationAgent/Audiences/?_id=${_id}&stageType=${stageType}&pageNumber=${pageNumber}&withEmailOnly=${withEmailOnly}`,
      providesTags: ['qualification'],
    }),

    getQualificationStageBreakdown: builder.query({
      query: agentId => `/QualificationAgent/StagesBreakdown/v1/?agentId=${agentId}`,
      providesTags: ['qualification'],
    }),

    getQualificationSignalsBreakdown: builder.query({
      query: agentId => `/QualificationAgent/SignalsBreakdown/v1/?agentId=${agentId}`,
      providesTags: ['qualification'],
    }),

    getQualificationVisitorsBreakdown: builder.query({
      query: agentId => `/QualificationAgent/VisitorsBreakdown/v1/?agentId=${agentId}`,
      providesTags: ['qualification'],
    }),

    // Smart Reply Feedback
    submitSmartReplyFeedback: builder.mutation({
      query: body => ({
        url: '/SmartReply/Agent/Feedback',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['smartReply'],
    }),

    // ---------------------- Folders ----------------------

    getAllFolders: builder.query({
      query: ({ eventId, workflowId } = {}) => {
        const params = eventId && workflowId ? `?eventId=${eventId}&workflowId=${workflowId}` : '';
        return `/Folder/FetchByUserId${params}`;
      },
      providesTags: ['folders', 'agents'],
    }),

    getFolderById: builder.query({
      query: arg => {
        const { folderId, eventId, workflowId } =
          typeof arg === 'string' ? { folderId: arg } : arg || {};
        const params = new URLSearchParams({ folderId });
        if (eventId && workflowId) {
          params.set('eventId', eventId);
          params.set('workflowId', workflowId);
        }
        return `/Folder/FetchById?${params.toString()}`;
      },
      providesTags: ['folders', 'agents'],
    }),
    addCampaignToFolder: builder.mutation({
      query: body => ({
        url: '/Folder/Addfolderitem',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['folders'],
    }),
    removeCampaignFromFolder: builder.mutation({
      query: body => ({
        url: '/Folder/Removefolderitem',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['folders'],
    }),

    createFolder: builder.mutation({
      query: body => ({
        url: '/Folder',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['folders'],
    }),

    updateFolder: builder.mutation({
      query: ({ _id, ...body }) => ({
        url: `/Folder?id=${_id}`,
        method: 'PUT',
        body: { _id, ...body },
      }),
      invalidatesTags: ['folders'],
    }),

    deleteFolder: builder.mutation({
      query: _id => ({
        url: `/Folder?id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['folders'],
    }),

    // ---------------------- Action Bucket ----------------------

    getAllActionBucket: builder.query({
      query: () => '/actionBucket',
      providesTags: ['actionBucket'],
    }),

    // ----------------------- Leads -----------------------

    getAllLeadsList: builder.mutation({
      query: body => ({
        url: '/Campaign/ContactsList',
        method: 'POST',
        body,
      }),
    }),

    // downloadLeadsList: builder.mutation({
    //   query: body => ({
    //     url: 'Submissions/download',
    //     method: 'POST',
    //     body,
    //   }),
    // }),

    // ----------------------- Profiles (Global profile management) -----------------------

    // getProfiles: builder.mutation({
    //   query: body => ({
    //     url: '/profile/table',
    //     method: 'POST',
    //     body,
    //   }),
    // }),

    getProfiles: builder.mutation({
      query: body => {
        const { eventId, ...requestBody } = body || {};
        const params = new URLSearchParams();
        if (eventId) params.set('eventId', eventId);
        const query = params.toString();

        return {
          url: `profile/SubmissionsTable${query ? `?${query}` : ''}`,
          method: 'POST',
          body: requestBody,
        };
      },
    }),

    getTargetList: builder.mutation({
      query: ({ body, agentId }) => ({
        url: `/targetList/search?agentId=${agentId}`,
        method: 'POST',
        body,
      }),
    }),

    downloadTargetList: builder.mutation({
      query: ({ agentId, body }) => ({
        url: `/targetList/download?agentId=${encodeURIComponent(agentId)}`,
        method: 'POST',
        body,
      }),
    }),

    uploadTargetListCsv: builder.mutation({
      query: formData => ({
        url: '/media/upload/csv',
        method: 'POST',
        body: formData,
      }),
    }),

    importTargetList: builder.mutation({
      query: body => ({
        url: '/targetList/import',
        method: 'POST',
        body,
      }),
    }),

    getTargetListImportStatus: builder.query({
      query: targetListImportId => ({
        url: `/targetList/import/status?targetListImportId=${encodeURIComponent(targetListImportId)}`,
        method: 'GET',
      }),
    }),

    getTargetListImportDetails: builder.query({
      query: targetListImportId => ({
        url: `/targetList/import?targetListImportId=${encodeURIComponent(targetListImportId)}`,
        method: 'GET',
      }),
    }),

    getTargetListMetaDataFields: builder.query({
      query: ({ agentId }) => ({
        url: `/targetList/metadata?agentId=${agentId}`,
        method: 'GET',
      }),
    }),

    downloadProfiles: builder.mutation({
      query: body => {
        const { eventId, ...requestBody } = body || {};
        const params = new URLSearchParams();
        if (eventId) params.set('eventId', eventId);
        const query = params.toString();

        return {
          url: `/profile/Submissions/download${query ? `?${query}` : ''}`,
          method: 'POST',
          body: requestBody,
        };
      },
    }),

    getProfilesMetaDataFields: builder.query({
      query: (arg = {}) => {
        const campaignId = typeof arg === 'string' ? arg : arg?.campaignId;
        const smartReplyAgentId =
          typeof arg === 'object' && arg !== null ? arg?.smartReplyAgentId : undefined;
        const eventId = typeof arg === 'object' && arg !== null ? arg?.eventId : undefined;
        const params = new URLSearchParams();
        if (campaignId) params.set('campaignId', campaignId);
        if (smartReplyAgentId) params.set('smartReplyAgentId', smartReplyAgentId);
        if (eventId) params.set('eventId', eventId);
        const query = params.toString();
        return {
          url: `/profile/GetMetaDataFields${query ? `?${query}` : ''}`,
          method: 'GET',
        };
      },
    }),

    // ----------------------- Draft ---------------------

    getAllDrafts: builder.query({
      query: type => `/Draft/FindAllByDraftType?draftType=${type}`,
      providesTags: ['draft'],
    }),

    getSingleDraft: builder.query({
      query: _id => `/Draft/FindById?_id=${_id}`,
      providesTags: ['draft'],
    }),

    createDraft: builder.mutation({
      query: body => ({
        url: '/Draft/Create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['draft'],
    }),

    deleteDraft: builder.mutation({
      query: _id => ({
        url: `/Draft/Delete?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['draft'],
    }),

    // ----------------------- AI Lab -----------------------

    getAllEngines: builder.query({
      query: () => '/Engine/FindAll',
      providesTags: ['aiLab'],
    }),

    getDefaultEngine: builder.query({
      query: () => '/Engine/FindAll?isDefault=true',
      providesTags: ['aiLab'],
    }),

    getNoDefaultEngine: builder.query({
      query: () => '/Engine/FindAll?isDefault=false',
      providesTags: ['aiLab'],
    }),

    getEngineById: builder.query({
      query: _id => `/Engine/FindById?_id=${_id}`,
      providesTags: ['aiLab'],
    }),

    deleteEngine: builder.mutation({
      query: _id => ({
        url: `/Engine/?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['aiLab'],
    }),

    createEngine: builder.mutation({
      query: body => ({
        url: '/Engine/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),

    updateEngine: builder.mutation({
      query: body => ({
        url: `/Engine?_id=${body?._id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),
    updateAndSelectEngine: builder.mutation({
      query: body => ({
        url: `/engine/version/editAndSelect`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),

    getEngineHistory: builder.query({
      query: _id => `/engine/versions?_id=${_id}`,
      providesTags: ['aiLab'],
    }),

    deleteEngineVersion: builder.mutation({
      query: _id => ({
        url: `/engine/version/delete?_id=${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['aiLab'],
    }),

    editEngineVersion: builder.mutation({
      query: body => ({
        url: `/engine/version/editTitle`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),

    duplicateEngine: builder.mutation({
      query: _id => ({
        url: `/Engine/Duplicate?_id=${_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['aiLab'],
    }),

    saveEngineVersion: builder.mutation({
      query: body => ({
        url: '/engine/saveAsVersion',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),

    rollbackEngineVersion: builder.mutation({
      query: body => ({
        url: '/Engine/version/select',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['aiLab'],
    }),

    // ---------------------- Matching engines (Connections) ----------------------
    // Runs, acceptance and who uses each matching engine (the engine list and cards).
    getMatchingEngineStats: builder.query({
      query: () => '/AILab/Matching/Stats',
      providesTags: ['aiLab', 'connection'],
    }),

    // One engine's outcomes per version, the agents that use it and its runs (D7).
    getMatchingEngineOutcomes: builder.query({
      query: engineId => `/AILab/Matching/Outcomes?engineId=${engineId}`,
      providesTags: ['aiLab', 'connection'],
    }),

    // Starts a test on up to 20 sample records of `collectionId` with the engine as it is in the
    // editor; nothing is saved to the engine. Answers at once with { testId, status: 'running', startedAt }.
    // `sessionsCollectionId` (optional): the event collection the sessions come from.
    testMatchingEngine: builder.mutation({
      query: body => ({
        url: '/AILab/Matching/Test',
        method: 'POST',
        body,
      }),
    }),

    // One test or simulation by `testId`, or the engine's latest by `engineId` (null when none):
    // { testId, engineId, versionId, kind, status: 'running'|'done'|'failed', startedAt, finishedAt?,
    // stage?, failedReason?, serverNow, ...the result when done }. Kept on the server for 30 days.
    // `serverNow` (ISO) is the server's clock, which the panel's timer counts on.
    getMatchingEngineTest: builder.query({
      query: ({ testId, engineId }) => ({
        url: '/AILab/Matching/Test',
        method: 'GET',
        params: testId ? { testId } : { engineId },
      }),
    }),

    // Links tests started on the "new engine" screen (no engine yet) to the engine once it's
    // published: body { engineId, testIds }, answers { linked }. Tests already linked are left alone.
    linkMatchingEngineTests: builder.mutation({
      query: body => ({
        url: '/AILab/Matching/Test/Link',
        method: 'POST',
        body,
      }),
    }),

    // The event's Connections workflow card: active, and how many agents run.
    getConnectionWorkflowStatus: builder.query({
      query: eventId => `/Connection/Agent/Status?eventId=${eventId}`,
      providesTags: ['connection', 'agents'],
    }),
    // ---------------------- end of matching engines ----------------------
    bulkSimulationHistory: builder.query({
      query: engineId => ({
        url: `/Engine/simulationFile/history?engineId=${engineId}`,
        method: 'GET',
      }),
    }),

    simulateEngine: builder.mutation({
      query: body => ({
        url: '/Engine/SimulateEngine',
        method: 'POST',
        body,
      }),
    }),

    simulateFAQCreations: builder.mutation({
      query: body => ({
        url: '/Engine/SimulateFAQCreations',
        method: 'POST',
        body,
      }),
    }),

    uploadSimulateEngineResult: builder.mutation({
      query: ({ engineId, actionBucketIds, body }) => {
        const ids = Array.isArray(actionBucketIds) ? actionBucketIds : [];
        const query = new URLSearchParams({ engineId: engineId || '' });
        ids.forEach(id => query.append('actionBucketIds', id));
        return {
          url: `/Engine/simulationFile/upload?${query.toString()}`,
          method: 'POST',
          body,
        };
      },
    }),

    startSimulationFile: builder.mutation({
      query: body => ({
        url: '/Engine/simulationFile/start',
        method: 'POST',
        body,
      }),
    }),

    getSimulateStatus: builder.mutation({
      query: simulationId => ({
        url: `/Engine/simulationFile/status?simulationId=${simulationId}`,
        method: 'GET',
      }),
    }),

    getSimulationResult: builder.mutation({
      query: simulationId => ({
        url: `/Engine/simulationFile/result?simulationId=${simulationId}`,
        method: 'GET',
      }),
    }),

    startLlmEval: builder.mutation({
      query: body => ({
        url: '/Engine/simulationFile/llmEval',
        method: 'POST',
        body,
      }),
    }),

    getLlmEvalStatus: builder.mutation({
      query: simulationId => ({
        url: `/Engine/simulationFile/llmEval/status?simulationId=${simulationId}`,
        method: 'GET',
      }),
    }),

    getLlmEvalResult: builder.mutation({
      query: evalId => ({
        url: `/Engine/simulationFile/llmEval/result?evalId=${evalId}`,
        method: 'GET',
      }),
    }),

    getLatestSimulate: builder.mutation({
      query: engineId => ({
        url: `/Engine/simulationFile/latest?engineId=${engineId}`,
        method: 'GET',
      }),
    }),

    generateGuideline: builder.mutation({
      query: body => ({
        url: '/Engine/GenerateGuideline',
        method: 'POST',
        body,
      }),
    }),

    checkSpamGuideline: builder.mutation({
      query: body => ({
        url: '/Engine/CheckEmailSpam',
        method: 'POST',
        body,
      }),
    }),

    createFineTuningJob: builder.mutation({
      query: body => ({
        url: 'engine/fineTuning',
        method: 'POST',
        body,
      }),
      transformResponse: response => {
        // The API answers { success, ... } (lowercase). Normalise to `Success` for the caller
        // instead of swapping in a mock, which turned a real `success: false` into "created".
        if (response && typeof response === 'object') {
          const success = response.Success ?? response.success;
          if (success !== undefined) return { ...response, Success: Boolean(success) };
        }
        // An empty 2xx body: the request went through.
        return { Success: true, data: response ?? null };
      },
      invalidatesTags: ['fineTuning'],
    }),

    getFineTuningJobs: builder.query({
      query: () => '/Engine/FineTuning',
      providesTags: ['fineTuning'],
    }),

    getEngineFeedbackPaginationWithFilter: builder.mutation({
      query: payload => ({
        url: '/EngineFeedback/GetByEngineInformation',
        method: 'POST',
        body: payload,
      }),
      providesTags: ['aiLab'],
    }),

    getFeedbackCountByType: builder.mutation({
      query: payload => ({
        url: '/EngineFeedback/GetFeedbackCountByType',
        method: 'POST',
        body: payload,
      }),
      providesTags: ['fineTuning'],
    }),

    getFeedbackCountByTypeTable: builder.mutation({
      query: payload => ({
        url: '/EngineFeedback/GetFeedbacks',
        method: 'POST',
        body: payload,
      }),
      //   // Mock data for table with pagination
      //   const { page = 0, limit = 15 } = payload || {};
      //   const allMockData = [
      //     {
      //       _id: '1',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Great response!',
      //       Source: 'Discuss with Sina',
      //       createdAt: new Date().toISOString(),
      //     },
      //     {
      //       _id: '2',
      //       engineTitle: 'Claude Engine',
      //       engineVersion: 'v2.0.1',
      //       feedbackType: 'Thumbs Down',
      //       feedbackText: 'Not accurate enough',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-14T14:20:00Z',
      //     },
      //     {
      //       _id: '3',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Manual Edits',
      //       feedbackText: 'Needs improvement in context understanding',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-13T09:15:00Z',
      //     },
      //     {
      //       _id: '4',
      //       engineTitle: 'Custom Engine',
      //       engineVersion: 'v3.1.0',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Excellent performance',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-12T16:45:00Z',
      //     },
      //     {
      //       _id: '5',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Thumbs Down',
      //       feedbackText: 'Response was too slow',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-11T11:30:00Z',
      //     },
      //     {
      //       _id: '6',
      //       engineTitle: 'Claude Engine',
      //       engineVersion: 'v2.0.1',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Very helpful',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-10T13:20:00Z',
      //     },
      //     {
      //       _id: '7',
      //       engineTitle: 'Custom Engine',
      //       engineVersion: 'v3.1.0',
      //       feedbackType: 'Manual Edits',
      //       feedbackText: 'Requires better formatting',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-09T08:10:00Z',
      //     },
      //     {
      //       _id: '8',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Perfect answer',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-08T15:25:00Z',
      //     },
      //     {
      //       _id: '9',
      //       engineTitle: 'Claude Engine',
      //       engineVersion: 'v2.0.1',
      //       feedbackType: 'Thumbs Down',
      //       feedbackText: 'Incorrect information',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-07T10:40:00Z',
      //     },
      //     {
      //       _id: '10',
      //       engineTitle: 'Custom Engine',
      //       engineVersion: 'v3.1.0',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Good quality response',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-06T12:50:00Z',
      //     },
      //     {
      //       _id: '11',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Manual Edits',
      //       feedbackText: 'Needs more detail',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-05T09:30:00Z',
      //     },
      //     {
      //       _id: '12',
      //       engineTitle: 'Claude Engine',
      //       engineVersion: 'v2.0.1',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Clear and concise',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-04T14:15:00Z',
      //     },
      //     {
      //       _id: '13',
      //       engineTitle: 'Custom Engine',
      //       engineVersion: 'v3.1.0',
      //       feedbackType: 'Thumbs Down',
      //       feedbackText: 'Missing key points',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-03T11:20:00Z',
      //     },
      //     {
      //       _id: '14',
      //       engineTitle: 'GPT-4 Engine',
      //       engineVersion: 'v1.2.3',
      //       feedbackType: 'Thumbs Up',
      //       feedbackText: 'Very accurate',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-02T16:35:00Z',
      //     },
      //     {
      //       _id: '15',
      //       engineTitle: 'Claude Engine',
      //       engineVersion: 'v2.0.1',
      //       feedbackType: 'Manual Edits',
      //       feedbackText: 'Should include examples',
      //       Source: 'Discuss with Sina',
      //       createdAt: '2024-01-01T10:00:00Z',
      //     },
      //   ];

      //   // Apply pagination
      //   const startIndex = page * limit;
      //   const endIndex = startIndex + limit;
      //   const paginatedData = allMockData.slice(startIndex, endIndex);

      //   return {
      //     data: {
      //       data: paginatedData,
      //       totalLength: allMockData.length,
      //     },
      //   };
      // },
      providesTags: ['fineTuning'],
    }),

    // ----------------------- Etc -----------------------
    uploadImage: builder.mutation({
      query: body => ({
        url: '/Media/image',
        method: 'POST',
        body,
      }),
    }),

    uploadDocument: builder.mutation({
      query: body => ({
        url: '/Media/document',
        method: 'POST',
        body,
      }),
    }),

    deleteImage: builder.mutation({
      query: body => ({
        url: '/Media',
        method: 'DELETE',
        body,
      }),
    }),

    Feedback: builder.mutation({
      query: data => ({
        url: '/Dashboard/Feedback',
        method: 'POST',
        body: data,
      }),
    }),

    Support: builder.mutation({
      query: data => ({
        url: '/Dashboard/Support',
        method: 'POST',
        body: data,
      }),
    }),

    // Credit Usage
    getCreditUsage: builder.query({
      query: () => '/credits/summary',
      providesTags: ['creditUsage'],
    }),

    getCreditUsageTimeline: builder.mutation({
      query: data => ({
        url: '/credits/timeline',
        method: 'POST',
        body: data,
      }),
    }),

    getTeamBrandingConfig: builder.query({
      query: () => '/team/brand',
      method: 'GET',
      // Branding can change outside this app; never keep a stale cache.
      keepUnusedDataFor: 0,
      providesTags: ['branding'],
    }),

    getAgentSummary: builder.mutation({
      query: data => ({
        url: '/credits/agentSummary',
        method: 'POST',
        body: data,
      }),
    }),

    getPlaybooks: builder.query({
      query: () => '/playbooks',
      method: 'GET',
    }),

    createBridgedEvent: builder.mutation({
      query: body => ({
        url: '/bridged-events',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['events'],
    }),

    getBridgedEvents: builder.query({
      query: ({ page = 1, limit = 12 } = {}) => ({
        url: '/bridged-events',
        method: 'GET',
        params: { page, limit },
      }),
      providesTags: ['events'],
    }),

    getBridgedEventById: builder.query({
      query: id => ({
        url: '/bridged-events',
        method: 'GET',
        params: { id },
      }),
      providesTags: ['events'],
    }),

    updateBridgedEvent: builder.mutation({
      query: body => ({
        url: '/bridged-events',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['events'],
    }),

    deleteBridgedEvent: builder.mutation({
      query: id => ({
        url: '/bridged-events',
        method: 'DELETE',
        params: { id },
      }),
      invalidatesTags: ['events'],
    }),
    refreshCreditUsage: builder.mutation({
      query: () => ({
        url: '/Credits/RefreshCreditUsage',
        method: 'POST',
      }),
    }),

    getCreditUsageCustomWorks: builder.mutation({
      query: () => ({
        url: '/Credits/CustomWork',
        method: 'GET',
      }),
    }),

    getSmartReplyTemplates: builder.query({
      query: agentId => ({
        url: `/SmartReply/Templates/?smartReplyAgentId=${agentId}`,
        method: 'GET',
      }),
    }),

    getLedgerHistory: builder.query({
      query: () => ({
        url: '/credits/TeamHistory',
        method: 'GET',
      }),
    }),
    //Sponsors
    getSponsorsList: builder.query({
      query: () => ({
        url: '/Sponsors',
        method: 'GET',
      }),
      invalidatesTags: ['sponsors'],
    }),

    getSponsorById: builder.query({
      query: ({ id }) => ({
        url: `/sponsors/detail?sponsorId=${id}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      invalidatesTags: ['sponsors'],
    }),

    createSponsor: builder.mutation({
      query: data => ({
        url: '/Sponsors',
        method: 'POST',
        body: data,
      }),
    }),

    updateSponsor: builder.mutation({
      query: ({ data, id }) => ({
        url: `/Sponsors?sponsorId=${id}`,
        method: 'PUT',
        body: data,
      }),
      providesTags: ['sponsors'],
    }),

    deleteSponsor: builder.mutation({
      query: ({ id }) => ({
        url: `/sponsors?sponsorId=${id}`,
        method: 'DELETE',
      }),
      providesTags: ['sponsors'],
    }),

    changeSponsorPassword: builder.mutation({
      query: ({ data, id }) => ({
        url: `/sponsors/resetPassword?sponsorId=${id}`,
        method: 'POST',
        body: data,
      }),
      providesTags: ['sponsors'],
    }),

    addSponsorEvent: builder.mutation({
      query: ({ data }) => ({
        url: `/sponsors/events`,
        method: 'POST',
        body: data,
      }),
    }),

    updateSponsorEvent: builder.mutation({
      query: ({ data, id }) => ({
        url: `/sponsors/events?eventId=${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    getSponsorEvents: builder.query({
      query: ({ id }) => ({
        url: `/sponsors/events?sponsorId=${id}`,
        method: 'GET',
      }),
    }),

    deleteSponsorEvent: builder.mutation({
      query: ({ id }) => ({
        url: `/sponsors/events?eventId=${id}`,
        method: 'DELETE',
      }),
    }),

    getCampaignByType: builder.mutation({
      query: () => ({
        url: `/Campaign/GetByCampaignType?campaignType=rag`,
        method: 'GET',
      }),
    }),

    getSmartReplyAgentsbyType: builder.query({
      query: () => ({
        url: `/SmartReply/GetBySmartReplyType?smartReplyType=whatsapp`,
        method: 'GET',
      }),
    }),
    getSmartReplyIntentMap: builder.query({
      query: ({ id }) => ({
        url: `/SmartReply/GetIntentMapping?smartReplyAgentId=${id}`,
        method: 'GET',
      }),
    }),

    getCampaignIntentMap: builder.mutation({
      query: ({ id }) => ({
        url: `/Campaign/GetIntentMapping?campaignId=${id}`,
        method: 'GET',
      }),
    }),

    createEventPackage: builder.mutation({
      query: ({ data }) => ({
        url: `/sponsors/packages`,
        method: 'POST',
        body: data,
      }),
    }),

    updateEventPackage: builder.mutation({
      query: ({ data, id }) => ({
        url: `/sponsors/packages?packageId=${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    // ----------------------- AB testing -----------------------
    getABTestGroups: builder.query({
      query: () => ({
        url: '/CampaignABTest/Group/list',
        method: 'GET',
      }),
      providesTags: ['abTest'],
    }),

    getABTestGroupById: builder.query({
      query: id => ({
        url: `/CampaignABTest/Group?groupId=${id}`,
        method: 'GET',
      }),
      providesTags: ['abTest'],
    }),

    createABTest: builder.mutation({
      query: body => ({
        url: '/CampaignABTest/Group',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['abTest'],
    }),

    toggleABTestStatus: builder.mutation({
      query: ({ id, isActive }) => ({
        url: `/CampaignABTest/Group/ToggleStatus`,
        method: 'PUT',
        body: {
          groupId: id,
          isActive: isActive,
        },
      }),
      invalidatesTags: ['abTest'],
    }),

    addVariantToABTest: builder.mutation({
      query: ({ groupId, newCampaignId }) => ({
        url: `/CampaignABTest/Group/AddVariant`,
        method: 'POST',
        body: {
          groupId: groupId,
          newCampaignId: newCampaignId,
        },
      }),
      invalidatesTags: ['abTest'],
    }),

    deleteABTestGroup: builder.mutation({
      query: ({ groupId }) => ({
        url: '/CampaignABTest/Group',
        method: 'DELETE',
        body: {
          groupId,
        },
      }),
      invalidatesTags: ['abTest'],
    }),

    // ----------------------- Integrations -----------------------
    //* Webhooks

    getWebhooks: builder.query({
      query: () => ({
        url: '/integration?type=ctaWebhook',
        method: 'GET',
      }),
      providesTags: ['webhooks'],
    }),

    createWebhook: builder.mutation({
      query: body => ({
        url: '/integration',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['webhooks'],
    }),

    updateWebhook: builder.mutation({
      query: ({ integrationId, body }) => ({
        url: `/integration?integrationId=${integrationId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['webhooks'],
    }),

    deleteWebhook: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration?integrationId=${integrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['webhooks'],
    }),

    getWebhookById: builder.query({
      query: ({ integrationId }) => ({
        url: `/integration/${integrationId}`,
        method: 'GET',
      }),
      providesTags: ['webhooks'],
    }),

    checkWebhookUpdate: builder.mutation({
      query: ({ integrationId, body }) => ({
        url: `/integration/ctaWebhook/update-check?integrationId=${integrationId}`,
        method: 'POST',
        body,
      }),
    }),

    checkWebhookDelete: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration/ctaWebhook/delete-check?integrationId=${integrationId}`,
        method: 'POST',
      }),
    }),

    testWebhook: builder.mutation({
      query: body => ({
        url: '/integration/ctaWebhook/test',
        method: 'POST',
        body,
      }),
    }),

    getWebhookLogs: builder.query({
      query: ({ integrationId, pageNumber = 1, limit = 10, sortOrder = 'desc' }) => ({
        url: `/WebhookLogs/FindAll?integrationId=${integrationId}&pageNumber=${pageNumber}&limit=${limit}&sortOrder=${sortOrder}`,
        method: 'GET',
      }),
      providesTags: ['webhookLogs'],
    }),

    // * Integration

    createIntegration: builder.mutation({
      query: ({ title }) => ({
        url: '/integration',
        method: 'POST',
        body: {
          title: title,
          integrationData: {
            type: 'whatsapp',
          },
        },
      }),
      invalidatesTags: ['Integrations'],
    }),

    startMetaWhatsappOnboarding: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration/meta/onboarding/start?integrationId=${integrationId}`,
        method: 'PUT',
        body: {},
      }),
    }),

    exchangeMetaWhatsappOnboarding: builder.mutation({
      query: ({ integrationId, body }) => ({
        url: `/integration/meta/onboarding/exchange?integrationId=${integrationId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Integrations'],
    }),

    cancelMetaWhatsappOnboarding: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration/meta/onboarding/cancel?integrationId=${integrationId}`,
        method: 'PUT',
        body: {},
      }),
    }),

    getAvailableIntegrations: builder.query({
      query: () => ({
        url: '/integration/meta/available',
        method: 'GET',
      }),
      providesTags: ['Integrations'],
    }),

    getIntegrationsList: builder.query({
      query: () => ({
        url: '/integration?type=whatsapp',
        method: 'GET',
      }),
      providesTags: ['Integrations'],
    }),

    deleteIntegration: builder.mutation({
      query: ({ integrationId }) => ({
        url: `/integration?integrationId=${integrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Integrations'],
    }),
  }),
});

// Export hooks for each endpoint
export const {
  // User
  useSignUpMutation,
  useLoginMutation,
  useGetPortalOAuthContextQuery,
  useApprovePortalOAuthMutation,
  useDenyPortalOAuthMutation,
  useGetBrandingConfigQuery,
  useResendVerifyEmailCodeMutation,
  useVerifyUserEmailMutation,
  useForgotPasswordVerificationMutation,
  useForgotPasswordMutation,
  useGoogleAuthenticationQuery,
  useFacebookAuthenticationQuery,
  // ---------- Dashboard ------------
  useDashboardOverviewMutation,
  useRefreshDashboardMutation,
  // ------------------  Onboarding --------------
  useGetAllTasksQuery,
  useUpdateTaskMutation,
  useGetTaskCommentsQuery,
  useCreateTaskCommentMutation,
  useUpdateTaskCommentMutation,
  useDeleteTaskCommentMutation,
  // ------------------  User --------------
  useUserInfoQuery,
  useUpdateUserInfoMutation,
  useDeleteUserAccountMutation,
  useChangeUserPasswordMutation,
  // ---------- CTAs ------------
  useCreateCtaMutation,
  useEditCtaMutation,
  useDeleteCtaMutation,
  useDuplicateCtaMutation,
  useGetAllUserCtasQuery,
  useGetCtaByIdQuery,
  // ---------- Engagements ------------
  useCreateEngMutation,
  useEditEngMutation,
  useDeleteEngMutation,
  useDuplicateEngMutation,
  useGetAllUserEngQuery,
  useGetEngByIdQuery,
  // ---------- AI Engagement ------------
  useGetAllAIEngQuery,
  useDeleteAiEngagementMutation,
  useApproveAIEngMutation,
  useUnApproveAIEngMutation,
  useRegenerateAIEngagementMutation,
  useEditAIEngMutation,
  // ---------- End Screen ------------
  useCreateEndScreenMutation,
  useEditEndScreenMutation,
  useDeleteEndScreenMutation,
  useDuplicateEndScreenMutation,
  useGetAllUserEndScreenQuery,
  useGetEndScreenByIdQuery,
  //  -------------- Collections V1 ------------------
  useCreateCollectionV1Mutation,
  useGetAllUserCollectionsQuery,
  useDeleteCollectionV1Mutation,
  useGetWordpressPluginConnectionQuery,
  useGetAllSitemapsMutation,
  useRenameCollectionMutation,
  useRefreshSitemapMutation,
  useGetXmlPageCrawlerStatusQuery,
  useStartXMLCrawlingMutation,
  useAddPageManuallyWithFileMutation,
  useGetTargetPageListMutation,

  //  -------------- New Collection ------------------
  useCreateCollectionMutation,
  useListCollectionsQuery,
  useGetCollectionByIdQuery,
  useDeleteCollectionMutation,
  useAddCollectionItemsMutation,
  useAddCollectionEntryPointItemsMutation,
  useStartCollectionIndexingMutation,
  useDeleteCollectionItemMutation,
  useDeleteCollectionEntryPointMutation,
  useRefreshCollectionMutation,
  useGetCollectionItemsQuery,
  useGetCollectionsModuleOverviewQuery,
  useGetCollectionRecordsQuery,
  usePreviewCollectionImportMutation,
  useImportCollectionRecordsMutation,
  useAddCollectionRecordMutation,
  useEditCollectionRecordMutation,
  useDeleteCollectionRecordMutation,
  useRequestCollectionEnrichmentMutation,
  useGetCollectionPushSettingsQuery,
  useCreateCollectionPushKeyMutation,
  useDeleteCollectionPushKeyMutation,
  useUpdateCollectionPushMappingMutation,
  useGetCollectionPushLogsQuery,
  useStartSitemapFetchMutation,
  useLazyGetSitemapFetchStatusQuery,
  useEditCollectionMutation,
  useEditCollectionItemMutation,
  useGetLabelsQuery,
  useCreateLabelMutation,
  useUpdateLabelMutation,
  useDeleteLabelMutation,
  useGetCollectionItemChunksQuery,
  useGetKnowledgeBaseChunkFilesQuery,
  useEditCollectionItemChunkMutation,
  useDeleteCollectionItemChunkMutation,

  //  -------------- Team ------------------
  useCreateTeamMutation,
  useGetTeamsQuery,
  useGetTeamByIdQuery,
  useInviteTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useDeleteTeamMemberMutation,
  useGetPendingTeamInvitesQuery,
  useAcceptTeamInviteMutation,
  useRejectTeamInviteMutation,
  useGetTeamAccessableSectionsQuery,
  // ---------- Domains ------------
  useGetAllDomainsQuery,
  useCreateDomainMutation,
  useVerifyDomainMutation,
  useDeleteDomainMutation,
  useUpdatePageMetaDataMutation,
  // ---------- Pages ------------
  useGetAllPagesMutation,
  useGetSinglePageDataQuery,
  useExcludePageFromCampaignMutation,
  // ---------- Analytics ------------
  useConversionRateChartMutation,
  useEngagementRateChartMutation,
  useViewsChartMutation,
  useTimeSpentMutation,
  useTopActionCardsMutation,
  useTopEngagementCardsMutation,
  useTopEndScreenCardsMutation,
  useTimeLineChartMutation,
  usePerformanceChartMutation,
  useCountriesChartMutation,
  useCitiesChartMutation,
  useBrowsersChartMutation,
  useDevicesChartMutation,
  // ---------- Agents ------------
  useUserConfigurationQuery,
  useAllAgentsQuery,
  useGetAgentByIdQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useDeleteAgentMutation,
  useEditAgentMutation,
  useDuplicateAgentMutation,
  useEditAgentStateMutation,
  useCreateScreenFromTemplateMutation,
  useRefreshAgentOverviewMutation,
  useGetAgentOverviewCacheStatusMutation,
  useAgentOverviewSummaryMutation,
  useAgentOverviewVoiceMutation,
  useDownloadPersonalizationToolRegistrationLeadsMutation,
  useAgentActivityMutation,
  useAgentOverviewTimelineMutation,

  useUploadFontMutation,
  useGetAllFontHistoryQuery,
  useAgentOverviewTopEngagementsMutation,
  useAgentOverviewTopEngagementsPaginationMutation,
  useAgentOverviewTopEngagementsDownloadMutation,
  useWhatsAppOverviewTopEngagementsMutation,
  useGetKAReferenceOverviewMutation,
  useAgentOverviewTopActionsMutation,
  useAgentIRISSessionsMutation,
  useAgentOverviewWordCloudMutation,
  useAgentOverviewWordCloudDownloadMutation,
  useAgentOverviewFeedbackMutation,
  useDownloadAgentOverviewFeedbackMutation,
  useAgentOverviewPagesMutation,
  useAgentOverviewPagesDownloadMutation,
  useAgentOverviewTopVisitorsQuery,
  useAgentOverviewTopVisitorsDynamicFormMutation,
  useAgentOverviewTopVisitorsJourneyCtasQuery,
  useAgentOverviewTopVisitorsDownloadMutation,
  useAIGeneratedExamplesMutation,
  useGetDomainConfigHistoryQuery,
  useClearFAQCacheMutation,
  useGetFAQKeywordConfigQuery,
  useDomainBrandSyncQuery,
  useGetAIBrandSyncSuggestionsMutation,
  // ---------------------- AI Agent ----------------------
  useAllAIAgentsQuery,
  useGetAIAgentByIdQuery,
  useCreateAIAgentMutation,
  useUpdateAIAgentMutation,
  useDeleteAIAgentMutation,
  // ---------------------- Smart Reply ----------------------
  useGetAllSmartRepliesQuery,
  useGetSmartReplyByIdQuery,
  useCreateSmartReplyMutation,
  useUpdateSmartReplyMutation,
  useDeleteSmartReplyMutation,
  // ---------------------- Smart Reply Overview ----------------------
  useGetSmartReplyOverviewQuery,
  useGetSmartReplyOverviewTimelineQuery,
  useGetSmartReplyEmailLogsQuery,
  useGetSmartReplyEmailThreadQuery,
  useGetSmartReplyTemplatesQuery,
  useGetLedgerHistoryQuery,
  useGetSmartReplyBroadcastListQuery,
  useDeleteSmartReplyBroadcastMutation,
  // ---------------------- Personalization Email Channel ----------------------
  useGetPtTargetListQuery,
  useGetPtTargetListMetaDataFieldsQuery,
  useImportProfilesFromCsvMutation,
  useGetCsvImportColumnsQuery,
  useImportProfilesFromHubspotMutation,
  useGetProfileImportStatusQuery,
  useGetPersonalizationProfileListQuery,
  useGetCrmIntegrationsListQuery,
  useGetIntegrationHubPropertiesQuery,
  useCreateHubspotPropertyMutation,
  useConnectEmailChannelHubspotMutation,
  useDeleteCrmIntegrationMutation,
  useVerifyHubspotIntegrationMutation,
  useGetPersonalizationAgentsQuery,
  useCreatePersonalizationAgentMutation,
  usePreviewPersonalizationAgentMutation,
  useStartPersonalizationAgentMutation,
  useGetPersonalizationAgentQuery,
  useDeletePersonalizationAgentMutation,
  useGetPersonalizationAgentProfilesQuery,
  useGetPersonalizationAgentProfileQuery,
  useRetryPersonalizationAgentFailedProfilesMutation,
  useDownloadPersonalizationAgentExportMutation,
  usePublishPersonalizationAgentIntegrationMutation,
  useGetPersonalizationAgentPublishStatusQuery,
  useGetPersonalizationAgentDeliveriesQuery,
  // Connection agent
  useCreateConnectionAgentMutation,
  useGetConnectionAgentQuery,
  useUpdateConnectionAgentMutation,
  useDeleteConnectionAgentMutation,
  useDuplicateConnectionAgentMutation,
  useStartConnectionRunMutation,
  useGetConnectionRunsQuery,
  usePublishConnectionAgentMutation,
  useGetConnectionMatchesQuery,
  useAddConnectionMatchMutation,
  useUpdateConnectionMatchMutation,
  useDeleteConnectionMatchMutation,
  useGetConnectionPeopleQuery,
  useGetConnectionCompaniesQuery,
  useUpdateConnectionCompanyMutation,
  useSendConnectionReminderMutation,
  useRemoveConnectionPersonMutation,
  useGetConnectionMeetingsQuery,
  useUpdateConnectionMeetingMutation,
  // Connection agent: the agent's page
  useGetConnectionOverviewQuery,
  useGetConnectionAgendaQuery,
  useCreateConnectionSessionMutation,
  useUpdateConnectionSessionMutation,
  useDeleteConnectionSessionMutation,
  useGetConnectionGroupsQuery,
  useMoveConnectionGroupMemberMutation,
  useReplaceConnectionPeerSuggestionsMutation,
  useAddConnectionPersonMutation,
  useUpdateConnectionPersonMutation,
  useCreateConnectionSignInLinkMutation,
  useResetConnectionPersonMutation,
  useGetConnectionSettingsQuery,
  useAddConnectionAdminMutation,
  useRemoveConnectionAdminMutation,
  useResetConnectionRoundMutation,
  // Connection agent: meetings and runs
  useGetConnectionMeetingQuery,
  useGetConnectionMeetingSlotsQuery,
  useDeleteConnectionMeetingMutation,
  usePreviewConnectionAutoAssignMutation,
  useRunConnectionAutoAssignMutation,
  useGetConnectionRunQuery,
  useGetConnectionFeedbackQuery,
  useGetConnectionReportQuery,
  useGetConnectionDeliveriesQuery,
  useCreateConnectionDeliveryMutation,
  useCancelConnectionDeliveryMutation,
  useGetConnectionDeliveryQuery,
  useGetConnectionDeliveryAudienceQuery,
  useUpdateConnectionDeliveryMutation,
  useDeleteConnectionDeliveryMutation,
  useSendConnectionDeliveryNowMutation,
  useCopyConnectionDeliveryMutation,
  useRetryConnectionDeliveryMutation,
  useSendConnectionDeliveryForRealMutation,
  useFixConnectionPersonContactMutation,
  useGetConnectionChannelsQuery,
  useUpdateConnectionChannelMutation,
  useVerifyConnectionSenderDomainMutation,
  useRequestConnectionWhatsappTemplateMutation,
  useCheckConnectionWhatsappTemplatesMutation,
  useGetConnectionMessagingQuery,
  useUpdateConnectionMessagingMutation,
  useUpdateConnectionAutoMessageMutation,
  useSendConnectionMessagingTestMutation,
  useGetConnectionPendingActionsQuery,
  useSendConnectionPendingActionsMutation,
  useSimulateMatchingEngineMutation,
  useGetPTInputLogsQuery,
  useGetPersonalizationByIdQuery,
  useGetPTTopChunksQuery,
  useGetPTFunnelOverviewQuery,
  useGetPTRecommendationTypesQuery,
  // ---------------------- Qualification ----------------------
  useGetAllQualificationAgentsQuery,
  useCreateQualificationMutation,
  useGetQualificationByIdQuery,
  useUpdateQualificationMutation,
  useDeleteQualificationMutation,

  // ---------------------- Qualification Agent v1 ----------------------
  useCreateQualificationAgentV1Mutation,
  useUpdateQualificationAgentV1Mutation,
  useGetQualificationAgentV1ByIdQuery,
  useGetAllQualificationAgentsV1Query,
  useDeleteQualificationAgentV1Mutation,
  // ---------------------- Qualification Overview ----------------------
  useGetQualificationOverviewQuery,
  useGetQualificationAudienceQuery,
  useGetQualificationStageBreakdownQuery,
  useGetQualificationSignalsBreakdownQuery,
  useGetQualificationVisitorsBreakdownQuery,
  useSubmitSmartReplyFeedbackMutation,
  // ---------------------- Folders ----------------------
  useGetAllFoldersQuery,
  useGetFolderByIdQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
  useAddCampaignToFolderMutation,
  useRemoveCampaignFromFolderMutation,
  // ---------------------- Action Bucket ----------------------
  useGetAllActionBucketQuery,
  useGetCollectionOverviewQuery,
  useUpdateAuthInfoMutation,
  useRefreshCtaFromCollectionMutation,
  useDeleteCtaFromCollectionMutation,
  useUpdateUseAsReferenceMutation,
  useDeleteMediaFromCollectionMutation,
  useUpdateMediaUseAsReferenceMutation,
  useAddPageToCollectionMutation,
  useAddMediaToCollectionMutation,
  useGetWordpressFiltersQuery,
  useUpdateMediaPreferredLinkMutation,
  // ----------------------- Leads -----------------------
  useGetAllLeadsListMutation,
  // useDownloadLeadsListMutation,
  // ----------------------- Profiles -----------------------
  useGetProfilesMutation,
  useGetTargetListMutation,
  useGetTargetListMetaDataFieldsQuery,
  useDownloadTargetListMutation,
  useUploadTargetListCsvMutation,
  useImportTargetListMutation,
  useGetTargetListImportStatusQuery,
  useGetTargetListImportDetailsQuery,
  useDownloadProfilesMutation,
  useGetProfilesMetaDataFieldsQuery,
  // ----------------------- Draft ---------------------
  useGetAllDraftsQuery,
  useGetSingleDraftQuery,
  useCreateDraftMutation,
  useDeleteDraftMutation,
  // ----------------------- AI Lab -----------------------
  useGetAllEnginesQuery,
  useGetDefaultEngineQuery,
  useGetEngineByIdQuery,
  useDeleteEngineMutation,
  useCreateEngineMutation,
  useUpdateEngineMutation,
  useSimulateEngineMutation,
  useSimulateFAQCreationsMutation,
  useGetLatestSimulateMutation,
  useStartSimulationFileMutation,
  useGetSimulateStatusMutation,
  useGetSimulationResultMutation,
  useStartLlmEvalMutation,
  useGetLlmEvalStatusMutation,
  useGetLlmEvalResultMutation,
  useUploadSimulateEngineResultMutation,
  useGenerateGuidelineMutation,
  useCheckSpamGuidelineMutation,
  useGetEngineHistoryQuery,
  useSaveEngineVersionMutation,
  useRollbackEngineVersionMutation,
  useDeleteEngineVersionMutation,
  useEditEngineVersionMutation,
  useDuplicateEngineMutation,
  // matching engines (Connections)
  useGetMatchingEngineStatsQuery,
  useGetMatchingEngineOutcomesQuery,
  useTestMatchingEngineMutation,
  useGetMatchingEngineTestQuery,
  useLazyGetMatchingEngineTestQuery,
  useLinkMatchingEngineTestsMutation,
  useGetConnectionWorkflowStatusQuery,
  useBulkSimulationHistoryQuery,
  useLazyBulkSimulationHistoryQuery,
  useUpdateAndSelectEngineMutation,
  useGetNoDefaultEngineQuery,
  // ----------------------- Fine Tuning -----------------------
  useCreateFineTuningJobMutation,
  useGetFineTuningJobsQuery,
  useGetEngineFeedbackPaginationWithFilterMutation,
  useGetFeedbackCountByTypeMutation,
  useGetFeedbackCountByTypeTableMutation,
  // ----------------------- Etc -----------------------
  useUploadImageMutation,
  useDeleteImageMutation,
  useFeedbackMutation,
  useSupportMutation,
  useUploadDocumentMutation,
  useGetTeamBrandingConfigQuery,

  // Credit Usage
  useGetCreditUsageQuery,
  useGetCreditUsageTimelineMutation,
  useGetAgentSummaryMutation,
  useGetPlaybooksQuery,
  useCreateBridgedEventMutation,
  useGetBridgedEventsQuery,
  useGetBridgedEventByIdQuery,
  useUpdateBridgedEventMutation,
  useDeleteBridgedEventMutation,
  useGetCreditUsageCustomWorksMutation,
  useRefreshCreditUsageMutation,
  useSetSmartReplyBroadcastMutation,

  //Sponsors
  useGetSponsorsListQuery,
  useCreateSponsorMutation,
  useUpdateSponsorMutation,
  useDeleteSponsorMutation,
  useChangeSponsorPasswordMutation,
  useAddSponsorEventMutation,
  useUpdateSponsorEventMutation,
  useGetSponsorByIdQuery,
  useGetSponsorEventsQuery,
  useDeleteSponsorEventMutation,
  useGetCampaignByTypeMutation,
  useGetSmartReplyAgentsbyTypeQuery,
  useGetSmartReplyIntentMapQuery,
  useLazyGetSmartReplyIntentMapQuery,
  useGetCampaignIntentMapMutation,
  useCreateEventPackageMutation,
  useUpdateEventPackageMutation,

  // ----------------------- AB testing -----------------------
  useGetABTestGroupsQuery,
  useGetABTestGroupByIdQuery,
  useCreateABTestMutation,
  useToggleABTestStatusMutation,
  useAddVariantToABTestMutation,
  useDeleteABTestGroupMutation,

  // ----------------------- Integrations -----------------------
  //* Webhooks
  useGetWebhooksQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useLazyGetWebhookByIdQuery,
  useGetWebhookByIdQuery,
  useCheckWebhookUpdateMutation,
  useCheckWebhookDeleteMutation,
  useTestWebhookMutation,
  useGetWebhookLogsQuery,

  // * Whatsapp integration
  useCreateIntegrationMutation,
  useStartMetaWhatsappOnboardingMutation,
  useExchangeMetaWhatsappOnboardingMutation,
  useCancelMetaWhatsappOnboardingMutation,
  useGetAvailableIntegrationsQuery,
  useGetIntegrationsListQuery,
  useDeleteIntegrationMutation,
} = bridgedApi;
